import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Paper,
  Autocomplete,
  RadioGroup,
  Radio,
  FormLabel,
  CircularProgress,
  Stepper,
  Step,
  StepButton,
} from "@mui/material";
import {
  Add as AddIcon,
  EmojiEvents as TrophyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  AccountTree as BracketIcon,
  Leaderboard as LeaderboardIcon,
  ViewStream as ViewStreamIcon,
  AccountTree as TreeIcon,
  Settings as SettingsIcon,
  Shuffle as ShuffleIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  Tournament,
  Player,
  TournamentLevel,
  TOURNAMENT_LEVELS,
  getTournamentLevels,
  getLevelRangeLabel,
  TournamentFormat,
  TournamentStatus,
  MatchScoringSystem,
  TournamentMatchFormat,
  KnockoutFormatType,
  AdvancementRule,
  TournamentEvent,
  TournamentEventType,
} from "../types";
import { apiRequest } from "../config/api";
import ImageUploadService from "../services/imageUpload";
import { useSeason } from "../contexts/SeasonContext";
import { getMapsUrl } from "../utils/maps";
import { isTestPlayerName } from "../utils/testPlayers";
import { useOrganization } from "../contexts/OrganizationContext";
import { getBracketNames } from "../utils/bracketNames";
import SeasonSelector from "../components/SeasonSelector";
import TournamentLevelSelector from "../components/TournamentLevelSelector";
import RichTextEditor from "../components/RichTextEditor";
import TimeField24 from "../components/TimeField24";
import TournamentPointsConfig from "./TournamentPointsConfig";
import TournamentScheduleDialog from "../components/TournamentScheduleDialog";
import KnockoutSeedingPreview from "../components/KnockoutSeedingPreview";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tournament-tabpanel-${index}`}
      aria-labelledby={`tournament-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TOURNAMENT_FORM_STEPS = ["Basics", "Format & rules", "Schedule", "Registration & money", "Visibility & sponsors", "Review"];
function Tournaments() {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState<TournamentLevel>("All");
  const { selectedSeason, allSeasons } = useSeason();
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  const isPE = currentOrg?.slug === "padel-eagle";
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    if (dialogOpen) setActiveStep(0);
  }, [dialogOpen]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [selectedTournamentForReg, setSelectedTournamentForReg] =
    useState<Tournament | null>(null);
  const [player1Id, setPlayer1Id] = useState("");
  const [player2Id, setPlayer2Id] = useState("");
  const [teamName, setTeamName] = useState("");
  const [viewTeamsDialogOpen, setViewTeamsDialogOpen] = useState(false);
  const [selectedTournamentForView, setSelectedTournamentForView] =
    useState<Tournament | null>(null);

  // Registrations overview
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);
  const [overviewTournament, setOverviewTournament] = useState<Tournament | null>(null);
  const [overviewData, setOverviewData] = useState<{ confirmedTeams: any[]; pendingInvitations: any[] } | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Player checklist
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistTournament, setChecklistTournament] = useState<Tournament | null>(null);
  const [checklist, setChecklist] = useState<{ [playerId: string]: { showed: boolean; paid: boolean } }>({});
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [checklistSearch, setChecklistSearch] = useState("");

  // Auto-approve tournament accounts toggle
  const [autoApproveTournament, setAutoApproveTournament] = useState(false);
  const [autoApproveLoading, setAutoApproveLoading] = useState(false);

  // Pot generation and draw animation states
  const [potDialogOpen, setPotDialogOpen] = useState(false);
  const [selectedTournamentForPots, setSelectedTournamentForPots] =
    useState<Tournament | null>(null);
  const [pots, setPots] = useState<
    {
      potNumber: number;
      teams: { id: string; name: string; avgLevel: number }[];
    }[]
  >([]);
  const [drawAnimationOpen, setDrawAnimationOpen] = useState(false);
  const [drawGroups, setDrawGroups] = useState<
    { name: string; teams: { id: string; name: string; avgLevel: number }[] }[]
  >([]);
  const [currentDrawStep, setCurrentDrawStep] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [standingsDialogOpen, setStandingsDialogOpen] = useState(false);
  const [selectedTournamentForStandings, setSelectedTournamentForStandings] =
    useState<Tournament | null>(null);
  const [draggedTeam, setDraggedTeam] = useState<{
    team: { id: string; name: string; avgLevel: number };
    sourceGroupIdx: number;
  } | null>(null);
  const [bracketDialogOpen, setBracketDialogOpen] = useState(false);
  const [selectedTournamentForBracket, setSelectedTournamentForBracket] =
    useState<Tournament | null>(null);
  const [bracketViewMode, setBracketViewMode] = useState<"mirror" | "tree">(
    "mirror",
  ); // Toggle between visualization modes
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [selectedMatchForScoring, setSelectedMatchForScoring] =
    useState<any>(null);
  const [team1Games, setTeam1Games] = useState(0);
  const [team2Games, setTeam2Games] = useState(0);
  const [bracketRefreshKey, setBracketRefreshKey] = useState(0); // Force re-render counter
  const [rankingsDialogOpen, setRankingsDialogOpen] = useState(false);
  const [selectedTournamentForRankings, setSelectedTournamentForRankings] =
    useState<Tournament | null>(null);
  const [tournamentRankings, setTournamentRankings] = useState<any>(null);

  const [displayModeDialogOpen, setDisplayModeDialogOpen] = useState(false);
  const [selectedTournamentForDisplay, setSelectedTournamentForDisplay] =
    useState<Tournament | null>(null);
  const [slideTimeDialogOpen, setSlideTimeDialogOpen] = useState(false);
  const [slideTime, setSlideTime] = useState(15); // kept for legacy reset
  const SLIDE_DEFS = [
    { key: "overview",  label: "Overview" },
    { key: "schedule",  label: "Schedule" },
    { key: "upcoming",  label: "Upcoming Matches" },
    { key: "results",   label: "Recent Results" },
    { key: "standings", label: "Group Standings" },
    { key: "brackets-primary",   label: "Knockout – Masters Cup" },
    { key: "brackets-secondary", label: "Knockout – Tour Cup" },
  ];
  const defaultSlideConfig = () => Object.fromEntries(SLIDE_DEFS.map(s => [s.key, { enabled: true, duration: 15 }]));
  const [slideConfig, setSlideConfig] = useState<Record<string, { enabled: boolean; duration: number }>>(defaultSlideConfig());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTournamentForDelete, setSelectedTournamentForDelete] =
    useState<Tournament | null>(null);
  const [keepScores, setKeepScores] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedTournamentForSchedule, setSelectedTournamentForSchedule] =
    useState<Tournament | null>(null);
  const [seedingPreviewOpen, setSeedingPreviewOpen] = useState(false);
  const [customSeeding, setCustomSeeding] = useState<import("../components/KnockoutSeedingPreview").CustomSeedingData>({});
  
  // Track expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTournamentId, setEditingTournamentId] = useState<string | null>(
    null,
  );

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [venue, setVenue] = useState("");
  const [tournamentLevels, setTournamentLevels] =
    useState<TournamentLevel[]>(["Open"]); // Levels for the tournament being created/edited (first = primary)
  const [tournamentSeasonId, setTournamentSeasonId] = useState<string | null>(
    null,
  ); // Season for the tournament being created/edited
  const [format, setFormat] = useState<TournamentFormat>("groups-knockout");
  const [scoringSystem, setScoringSystem] =
    useState<MatchScoringSystem>("sets-games");
  const [groupMatchFormat, setGroupMatchFormat] = useState<TournamentMatchFormat>("best-of-7");
  const [knockoutMatchFormat, setKnockoutMatchFormat] = useState<TournamentMatchFormat>("best-of-7");
  const [timedMatchDuration, setTimedMatchDuration] = useState<number | "">(20); // minutes
  const [changeoverTime, setChangeoverTime] = useState<number | "">(0); // minutes between matches
  const [numberOfTeams, setNumberOfTeams] = useState<number | "">(16);
  const [teamsPerGroup, setTeamsPerGroup] = useState<number | "">(4);
  const [knockoutFormatType, setKnockoutFormatType] =
    useState<KnockoutFormatType>("single-bracket");
  const [mastersCupAdvanceCount, setMastersCupAdvanceCount] = useState<number | "">(2); // Top 2 to Masters Cup
  const [tourCupAdvanceCount, setTourCupAdvanceCount] = useState<number | "">(2); // Next 2 to Tour Cup
  const [advancedAdvancementEnabled, setAdvancedAdvancementEnabled] = useState(false); // Cross-group rules (e.g. "best runner-up") instead of a fixed count per group
  const [crossGroupRules, setCrossGroupRules] = useState<Array<{ position: number; count: number }>>([]); // "best-of-position-across-groups" rules feeding Masters Cup; Tour Cup always gets the remainder
  const [knockoutAdvanceCount, setKnockoutAdvanceCount] = useState<number | "">(2); // Teams advancing from each group

  // Shared with both the submit payload and the seeding preview so they never drift apart.
  const getMastersCupAdvancementRules = (): AdvancementRule[] | undefined => {
    if (!advancedAdvancementEnabled || typeof mastersCupAdvanceCount !== "number") return undefined;
    return [
      ...Array.from({ length: mastersCupAdvanceCount }, (_, i): AdvancementRule => ({
        type: "group-position",
        position: i,
      })),
      ...crossGroupRules.map((r): AdvancementRule => ({
        type: "best-of-position-across-groups",
        position: r.position,
        count: r.count,
      })),
    ];
  };
  const getTourCupAdvancementRules = (): AdvancementRule[] | undefined =>
    advancedAdvancementEnabled ? [{ type: "remaining" }] : undefined;
  const [playAllPositions, setPlayAllPositions] = useState(true);
  const [isPointsRelevant, setIsPointsRelevant] = useState(true);
  const [registrationOpenDate, setRegistrationOpenDate] = useState<string>("");
  const [registrationCloseDate, setRegistrationCloseDate] =
    useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("09:00");
  const [endDate, setEndDate] = useState<string>("");
  const [numberOfCourts, setNumberOfCourts] = useState<number | "">(4);
  const [courtPricePerSlot, setCourtPricePerSlot] = useState<number | "">("");
  const [registrationFeePerTeam, setRegistrationFeePerTeam] = useState<number | "">("");
  const [events, setEvents] = useState<TournamentEvent[]>([]);
  const [visibilityMode, setVisibilityMode] = useState<"all" | "specific" | "hidden">("all");
  const [visibleToPlayerIds, setVisibleToPlayerIds] = useState<string[]>([]);
  const [playerRegistrationManagedByAdmin, setPlayerRegistrationManagedByAdmin] = useState(false);
  const [posterImageUrl, setPosterImageUrl] = useState<string>("");
  const [posterPreview, setPosterPreview] = useState<string>("");
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [sponsors, setSponsors] = useState<{ name: string; logoUrl: string; websiteUrl: string; uploading: boolean }[]>([]);
  
  // Event dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<TournamentEvent | null>(null);
  const [eventName, setEventName] = useState("");
  const [eventType, setEventType] = useState<TournamentEventType>("break");
  const [eventStartTime, setEventStartTime] = useState("");
  const [eventDuration, setEventDuration] = useState<number | "">(15);
  const [eventDescription, setEventDescription] = useState("");

  useEffect(() => {
    fetchTournaments();
    fetchPlayers();
  }, [selectedLevel, selectedSeason]);

  useEffect(() => {
    apiRequest("/api/tournament-settings/registration-config")
      .then(r => r.json())
      .then(data => setAutoApproveTournament(data.autoApproveTournamentAccounts ?? false))
      .catch(() => {});
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setTournaments([]); // Reset to empty array
      const params = new URLSearchParams();
      if (selectedSeason?.id) params.append("seasonId", selectedSeason.id);
      if (selectedLevel !== "All") params.append("level", selectedLevel);

      const response = await apiRequest(`/api/tournaments?${params}`);
      const data = await response.json();
      setTournaments(Array.isArray(data) ? data : []);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load tournaments");
      setTournaments([]); // Ensure it's an array even on error
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const params = new URLSearchParams();
      params.append("activeOnly", "true");

      const response = await apiRequest(`/api/players?${params}`);
      const data = await response.json();
      setPlayers(data);
    } catch (err) {
      console.error("Failed to load players:", err);
    }
  };

  // Per-step checks for the create stepper (final submit still runs the full validation below)
  const validateTournamentStep = (stepIndex: number): string | null => {
    if (stepIndex === 0 && !name) return "Please enter a tournament name";
    if (stepIndex === 1) {
      if (typeof numberOfTeams !== "number" || numberOfTeams < 2) return "Please enter the number of teams (at least 2)";
      if (format === "groups-knockout") {
        if (typeof teamsPerGroup !== "number") return "Please enter the teams per group";
        if (numberOfTeams % teamsPerGroup !== 0) {
          return `${numberOfTeams} teams cannot be evenly divided into groups of ${teamsPerGroup}. Please adjust the numbers.`;
        }
      }
    }
    if (stepIndex === 2 && !startDate) return "Please select the tournament start date";
    if (stepIndex === 3 && (!registrationOpenDate || !registrationCloseDate)) {
      return "Please select both date and time for when registration opens and closes (a partially filled field counts as empty)";
    }
    return null;
  };

  const handleCreateTournament = async () => {
    if (
      !name ||
      !registrationOpenDate ||
      !registrationCloseDate ||
      !startDate
    ) {
      showSnackbar("Please fill in all required fields");
      return;
    }

    // Validation for groups-knockout format
    if (format === "groups-knockout") {
      if (typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number') {
        // Check if teams can be evenly divided into groups
        if (numberOfTeams % teamsPerGroup !== 0) {
          showSnackbar(`${numberOfTeams} teams cannot be evenly divided into groups of ${teamsPerGroup}. Please adjust the numbers.`);
          return;
        }
      }

      // Validation for double-bracket format
      if (knockoutFormatType === "double-bracket") {
        if (advancedAdvancementEnabled) {
          // Tour Cup always absorbs the remainder, so no sum-vs-teamsPerGroup check is needed —
          // just make sure no cross-group rule asks for more teams than there are groups to pick from.
          const groupCount =
            typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && teamsPerGroup > 0
              ? numberOfTeams / teamsPerGroup
              : undefined;
          const invalidRule = crossGroupRules.find(r => typeof groupCount === 'number' && r.count > groupCount);
          if (invalidRule) {
            showSnackbar(`Cannot pick the best ${invalidRule.count} team(s) at position ${invalidRule.position + 1} — there are only ${groupCount} groups.`);
            return;
          }
        } else if (typeof teamsPerGroup === 'number' && typeof mastersCupAdvanceCount === 'number' && typeof tourCupAdvanceCount === 'number') {
          if (mastersCupAdvanceCount + tourCupAdvanceCount > teamsPerGroup) {
            showSnackbar(`${bracketNames.primary} (${mastersCupAdvanceCount}) + ${bracketNames.secondary} (${tourCupAdvanceCount}) teams cannot exceed ${teamsPerGroup} teams per group.`);
            return;
          }
        }
      }

      // Validation for single-bracket format
      if (knockoutFormatType === "single-bracket") {
        if (typeof teamsPerGroup === 'number' && typeof knockoutAdvanceCount === 'number') {
          if (knockoutAdvanceCount >= teamsPerGroup) {
            showSnackbar(`Cannot advance ${knockoutAdvanceCount} teams from groups of ${teamsPerGroup}. Maximum is ${teamsPerGroup - 1}.`);
            return;
          }
        }
      }
    }

    try {
      const tournamentData = {
        name,
        description,
        venue: venue || undefined,
        level: tournamentLevels[0] || "Open",
        levels: tournamentLevels.length > 0 ? tournamentLevels : ["Open"],
        seasonId: tournamentSeasonId || undefined, // Use the season from the form dropdown
        format,
        scoringSystem,
        groupMatchFormat: scoringSystem === "sets-games" ? groupMatchFormat : undefined,
        knockoutMatchFormat: scoringSystem === "sets-games" ? knockoutMatchFormat : undefined,
        matchFormat: undefined,
        timedMatchDuration: timedMatchDuration || undefined,
        changeoverTime, // Minutes between matches on same court
        maxTeams: numberOfTeams,
        minTeams: numberOfTeams,
        teamsPerGroup: format === "groups-knockout" ? teamsPerGroup : undefined,
        knockoutFormatType:
          format === "groups-knockout" ? knockoutFormatType : undefined,
        mastersCupAdvanceCount:
          format === "groups-knockout" &&
          knockoutFormatType === "double-bracket"
            ? mastersCupAdvanceCount
            : undefined,
        tourCupAdvanceCount:
          format === "groups-knockout" &&
          knockoutFormatType === "double-bracket"
            ? tourCupAdvanceCount
            : undefined,
        knockoutAdvanceCount:
          format === "groups-knockout" &&
          knockoutFormatType === "single-bracket"
            ? knockoutAdvanceCount
            : undefined,
        // `null` (not `undefined`) on purpose when there are no rules: JSON.stringify drops
        // `undefined` keys entirely, so a PUT update would never tell the backend to clear a
        // previously-saved rules array — the checkbox would stay checked forever once enabled.
        mastersCupAdvancementRules:
          format === "groups-knockout" && knockoutFormatType === "double-bracket"
            ? (getMastersCupAdvancementRules() ?? null)
            : null,
        tourCupAdvancementRules:
          format === "groups-knockout" && knockoutFormatType === "double-bracket"
            ? (getTourCupAdvancementRules() ?? null)
            : null,
        playAllPositions,
        isPointsRelevant,
        registrationOpenDate: new Date(registrationOpenDate),
        registrationCloseDate: new Date(registrationCloseDate),
        startDate: new Date(startDate),
        startTime, // HH:MM format
        endDate: endDate ? new Date(endDate) : undefined,
        numberOfCourts,
        courtPricePerSlot: courtPricePerSlot !== "" ? courtPricePerSlot : undefined,
        registrationFeePerTeam: registrationFeePerTeam !== "" ? registrationFeePerTeam : undefined,
        events,
        isVisibleToPlayers: visibilityMode !== "hidden",
        visibleToPlayerIds: visibilityMode === "specific" ? visibleToPlayerIds : [],
        playerRegistrationManagedByAdmin,
        posterImageUrl: posterImageUrl || undefined,
        sponsors: sponsors.filter(s => s.logoUrl).map(({ name, logoUrl, websiteUrl }) => ({ name, logoUrl, ...(websiteUrl ? { websiteUrl } : {}) })),
        pointsPerWin: 3,
        pointsPerDraw: 1,
        pointsPerLoss: 0,
        // Custom knockout seeding (if admin rearranged in preview). `null` (not `undefined`) when
        // empty so a PUT update actually clears a previously-saved override — see the
        // mastersCupAdvancementRules fields above for why `undefined` can't do that.
        ...(format === "groups-knockout" ? {
          customMastersCupSeeding: customSeeding.mastersCup || null,
          customTourCupSeeding: customSeeding.tourCup || null,
          customSingleBracketSeeding: customSeeding.singleBracket || null,
        } : {}),
      };

      const url = isEditMode
        ? `/api/tournaments/${editingTournamentId}`
        : "/api/tournaments";
      const method = isEditMode ? "PUT" : "POST";

      await apiRequest(url, {
        method,
        body: JSON.stringify(tournamentData),
      });

      showSnackbar(
        isEditMode
          ? "Tournament updated successfully!"
          : "Tournament created successfully!",
      );
      setDialogOpen(false);
      resetForm();
      fetchTournaments();
    } catch (err) {
      showSnackbar(
        isEditMode
          ? "Failed to update tournament"
          : "Failed to create tournament",
      );
      console.error(err);
    }
  };

  const handleEditTournament = (tournament: Tournament) => {
    setIsEditMode(true);
    setEditingTournamentId(tournament.id);
    setName(tournament.name);
    setDescription(tournament.description || "");
    setVenue(tournament.venue || "");
    setTournamentLevels(getTournamentLevels(tournament));
    setTournamentSeasonId(tournament.seasonId || null);
    setFormat(tournament.format);
    setScoringSystem(tournament.scoringSystem);
    // Load per-stage formats; fall back to legacy matchFormat for old tournaments
    const legacyFmt = (tournament.matchFormat as TournamentMatchFormat) || "best-of-7";
    setGroupMatchFormat(tournament.groupMatchFormat || legacyFmt);
    setKnockoutMatchFormat(tournament.knockoutMatchFormat || legacyFmt);
    setTimedMatchDuration(tournament.timedMatchDuration || 20);
    setChangeoverTime(tournament.changeoverTime || 0);
    setNumberOfTeams(tournament.maxTeams || 16);
    setTeamsPerGroup(tournament.teamsPerGroup || 4);
    setKnockoutFormatType(tournament.knockoutFormatType || "single-bracket");
    setMastersCupAdvanceCount(tournament.mastersCupAdvanceCount || 2);
    setTourCupAdvanceCount(tournament.tourCupAdvanceCount || 2);
    setKnockoutAdvanceCount(tournament.knockoutAdvanceCount || 2);
    setAdvancedAdvancementEnabled(
      !!(tournament.mastersCupAdvancementRules?.length || tournament.tourCupAdvancementRules?.length)
    );
    setCrossGroupRules(
      (tournament.mastersCupAdvancementRules || [])
        .filter((r): r is Extract<AdvancementRule, { type: 'best-of-position-across-groups' }> => r.type === 'best-of-position-across-groups')
        .map(r => ({ position: r.position, count: r.count }))
    );
    setPlayAllPositions(tournament.playAllPositions !== false);
    setIsPointsRelevant(tournament.isPointsRelevant !== false);
    if (tournament.isVisibleToPlayers === false) {
      setVisibilityMode("hidden");
      setVisibleToPlayerIds([]);
    } else if (tournament.visibleToPlayerIds && tournament.visibleToPlayerIds.length > 0) {
      setVisibilityMode("specific");
      setVisibleToPlayerIds(tournament.visibleToPlayerIds);
    } else {
      setVisibilityMode("all");
      setVisibleToPlayerIds([]);
    }
    setPlayerRegistrationManagedByAdmin(tournament.playerRegistrationManagedByAdmin === true);
    setPosterImageUrl((tournament as any).posterImageUrl || "");
    setPosterPreview((tournament as any).posterImageUrl || "");
    setSponsors(((tournament as any).sponsors || []).map((s: any) => ({ ...s, websiteUrl: s.websiteUrl || "", uploading: false })));
    // Restore custom seeding if it was saved
    setCustomSeeding({
      mastersCup: (tournament as any).customMastersCupSeeding || undefined,
      tourCup: (tournament as any).customTourCupSeeding || undefined,
      singleBracket: (tournament as any).customSingleBracketSeeding || undefined,
    });

    // Format dates for input fields (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (date: Date | string) => {
      const d = new Date(date);
      return d.toISOString().slice(0, 16);
    };

    setRegistrationOpenDate(
      formatDateForInput(tournament.registrationOpenDate),
    );
    setRegistrationCloseDate(
      formatDateForInput(tournament.registrationCloseDate),
    );
    setStartDate(formatDateForInput(tournament.startDate).slice(0, 10)); // <input type="date"> needs YYYY-MM-DD
    setStartTime(tournament.startTime || "09:00");
    if (tournament.endDate) {
      setEndDate(formatDateForInput(tournament.endDate).slice(0, 10));
    }
    setNumberOfCourts(tournament.numberOfCourts || 4);
    setCourtPricePerSlot(tournament.courtPricePerSlot ?? "");
    setRegistrationFeePerTeam(tournament.registrationFeePerTeam ?? "");
    setEvents(tournament.events || []);

    setDialogOpen(true);
  };

  const handleOpenRegistration = async (tournament: Tournament) => {
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ status: "registration" }),
      });

      showSnackbar("Registration opened!");
      fetchTournaments();
    } catch (err) {
      showSnackbar("Failed to open registration");
      console.error(err);
    }
  };

  const handleToggleRegistration = async (tournament: Tournament) => {
    const isCurrentlyOpen = tournament.status === "registration";
    const newStatus = isCurrentlyOpen ? "upcoming" : "registration";

    // Optimistic update so the toggle flips immediately
    setTournaments(prev =>
      prev.map(t => t.id === tournament.id ? { ...t, status: newStatus as any } : t)
    );

    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({
          status: newStatus,
          // When admin manually closes during an active window, tell the backend
          // not to re-open it automatically on the next fetch.
          registrationAutoManaged: isCurrentlyOpen ? false : true,
        }),
      });
      showSnackbar(isCurrentlyOpen ? "Registration closed." : "Registration opened! Tournament is now visible to players.");
      fetchTournaments();
    } catch (err) {
      // Revert optimistic update on failure
      setTournaments(prev =>
        prev.map(t => t.id === tournament.id ? { ...t, status: tournament.status } : t)
      );
      showSnackbar("Failed to update registration status.");
      console.error(err);
    }
  };

  const handleGenerateGroups = async (tournament: Tournament) => {
    try {
      const response = await apiRequest(
        `/api/tournaments/${tournament.id}/generate-groups`,
        {
          method: "POST",
        },
      );
      const result: any = await response.json();

      showSnackbar(`Generated ${result.groups.length} groups!`);
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to generate groups");
      console.error(err);
    }
  };

  const handleOpenRegisterDialog = (tournament: Tournament) => {
    setSelectedTournamentForReg(tournament);
    setPlayer1Id("");
    setPlayer2Id("");
    setTeamName("");
    setRegisterDialogOpen(true);
  };

  const handleRegisterTeam = async () => {
    if (!player1Id || !player2Id) {
      showSnackbar("Please select both players");
      return;
    }

    if (player1Id === player2Id) {
      showSnackbar("Please select different players");
      return;
    }

    try {
      await apiRequest(
        `/api/tournaments/${selectedTournamentForReg!.id}/register`,
        {
          method: "POST",
          body: JSON.stringify({
            player1Id,
            player2Id,
            teamName: teamName || undefined,
          }),
        },
      );

      showSnackbar("Team registered successfully!");
      setRegisterDialogOpen(false);
      setPlayer1Id("");
      setPlayer2Id("");
      setTeamName("");
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to register team");
      console.error(err);
    }
  };

  const handleRandomTeamSelection = () => {
    // Get all already registered player IDs
    const registeredPlayerIds = new Set(
      selectedTournamentForReg?.registeredTeams?.flatMap((team) => [
        team.player1Id,
        team.player2Id,
      ]) || []
    );

    // Filter available players (not already registered)
    const availablePlayers = players.filter(
      (player) => !registeredPlayerIds.has(player.id)
    );

    if (availablePlayers.length < 2) {
      showSnackbar("Not enough available players to form a team");
      return;
    }

    // Randomly select 2 players
    const shuffled = [...availablePlayers].sort(() => Math.random() - 0.5);
    const randomPlayer1 = shuffled[0];
    const randomPlayer2 = shuffled[1];

    setPlayer1Id(randomPlayer1.id);
    setPlayer2Id(randomPlayer2.id);
    showSnackbar(`Selected: ${randomPlayer1.name} & ${randomPlayer2.name}`);
  };

  const handleFillWithTestTeams = async () => {
    if (!selectedTournamentForReg) return;

    const spotsLeft =
      (selectedTournamentForReg.maxTeams || 0) -
      (selectedTournamentForReg.registeredTeams?.length || 0);

    if (spotsLeft <= 0) {
      showSnackbar("Tournament is already full");
      return;
    }

    const registeredPlayerIds = new Set(
      selectedTournamentForReg.registeredTeams?.flatMap((t) => [
        t.player1Id,
        t.player2Id,
      ]) || []
    );

    const testPlayers = players.filter(
      (p) => isTestPlayerName(p.name, currentOrg?.slug) && !registeredPlayerIds.has(p.id)
    );

    if (testPlayers.length < 2) {
      showSnackbar("Not enough test players available");
      return;
    }

    const shuffled = [...testPlayers].sort(() => Math.random() - 0.5);
    const teamsToGenerate = Math.min(spotsLeft, Math.floor(shuffled.length / 2));

    try {
      for (let i = 0; i < teamsToGenerate * 2; i += 2) {
        await apiRequest(
          `/api/tournaments/${selectedTournamentForReg.id}/register`,
          {
            method: "POST",
            body: JSON.stringify({
              player1Id: shuffled[i].id,
              player2Id: shuffled[i + 1].id,
            }),
          }
        );
      }
      showSnackbar(`Filled ${teamsToGenerate} spot${teamsToGenerate !== 1 ? "s" : ""} with test teams`);
      setRegisterDialogOpen(false);
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to fill with test teams");
    }
  };

  const handleGenerateRandomTeams = async (numTeams: number = 24) => {
    if (!selectedTournamentForReg) return;

    // Get all already registered player IDs
    const registeredPlayerIds = new Set(
      selectedTournamentForReg.registeredTeams?.flatMap((team) => [
        team.player1Id,
        team.player2Id,
      ]) || []
    );

    // Filter available players (not already registered)
    const availablePlayers = players.filter(
      (player) => !registeredPlayerIds.has(player.id)
    );

    const maxPossibleTeams = Math.floor(availablePlayers.length / 2);
    const teamsToGenerate = Math.min(numTeams, maxPossibleTeams);

    if (availablePlayers.length < 2) {
      showSnackbar("Not enough available players to form teams");
      return;
    }

    if (teamsToGenerate === 0) {
      showSnackbar("Not enough available players to form any teams");
      return;
    }

    // Confirm with user
    const confirmation = window.confirm(
      `This will generate ${teamsToGenerate} random teams from ${availablePlayers.length} available players. Continue?`
    );

    if (!confirmation) return;

    try {
      // Shuffle all available players
      const shuffled = [...availablePlayers].sort(() => Math.random() - 0.5);

      // Generate teams
      const teamsToRegister = [];
      for (let i = 0; i < teamsToGenerate * 2; i += 2) {
        if (i + 1 < shuffled.length) {
          teamsToRegister.push({
            player1Id: shuffled[i].id,
            player2Id: shuffled[i + 1].id,
          });
        }
      }

      // Register all teams
      for (const team of teamsToRegister) {
        await apiRequest(
          `/api/tournaments/${selectedTournamentForReg.id}/register`,
          {
            method: "POST",
            body: JSON.stringify({
              player1Id: team.player1Id,
              player2Id: team.player2Id,
            }),
          }
        );
      }

      showSnackbar(`Successfully generated ${teamsToRegister.length} random teams!`);
      setRegisterDialogOpen(false);
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to generate teams");
      console.error(err);
    }
  };

  const handleOpenViewTeamsDialog = (tournament: Tournament) => {
    setSelectedTournamentForView(tournament);
    setViewTeamsDialogOpen(true);
  };

  const handleOpenPotDialog = (tournament: Tournament) => {
    setSelectedTournamentForPots(tournament);
    generatePots(tournament);
    setPotDialogOpen(true);
  };

  const generatePots = (tournament: Tournament) => {
    const teams = tournament.registeredTeams || [];
    const teamsPerGroup = tournament.teamsPerGroup || 4;
    const numGroups = Math.ceil(teams.length / teamsPerGroup);
    const numPots = teamsPerGroup;

    // Calculate average level for each team
    const teamsWithLevel = teams.map((team) => {
      const player1 = players.find((p) => p.id === team.player1Id);
      const player2 = players.find((p) => p.id === team.player2Id);
      const level1 = player1?.level || 0;
      const level2 = player2?.level || 0;
      const avgLevel = (level1 + level2) / 2;

      // Use names from team object first, fall back to player lookup
      const player1Name = team.player1Name || player1?.name || "Player 1";
      const player2Name = team.player2Name || player2?.name || "Player 2";
      const name = team.teamName || `${player1Name} & ${player2Name}`;

      return { id: team.id, name, avgLevel };
    });

    // Sort teams by average level (descending)
    teamsWithLevel.sort((a, b) => b.avgLevel - a.avgLevel);

    // Distribute teams into pots (World Cup style)
    const newPots: {
      potNumber: number;
      teams: { id: string; name: string; avgLevel: number }[];
    }[] = [];
    for (let i = 0; i < numPots; i++) {
      newPots.push({ potNumber: i + 1, teams: [] });
    }

    // Assign teams to pots in World Cup style:
    // Pot 1: Top numGroups teams (e.g., teams 0-2 if 3 groups)
    // Pot 2: Next numGroups teams (e.g., teams 3-5)
    // Pot 3: Next numGroups teams (e.g., teams 6-8)
    // Pot 4: Remaining teams (e.g., teams 9-11)
    teamsWithLevel.forEach((team, index) => {
      const potIndex = Math.floor(index / numGroups);
      if (potIndex < numPots) {
        newPots[potIndex].teams.push(team);
      }
    });

    setPots(newPots);
  };

  const moveTeamBetweenPots = (
    teamId: string,
    fromPot: number,
    toPot: number,
  ) => {
    const newPots = [...pots];
    const fromPotIndex = newPots.findIndex((p) => p.potNumber === fromPot);
    const toPotIndex = newPots.findIndex((p) => p.potNumber === toPot);

    const teamIndex = newPots[fromPotIndex].teams.findIndex(
      (t) => t.id === teamId,
    );
    const team = newPots[fromPotIndex].teams[teamIndex];

    newPots[fromPotIndex].teams.splice(teamIndex, 1);
    newPots[toPotIndex].teams.push(team);

    setPots(newPots);
  };

  const startDraw = () => {
    const teamsPerGroup = selectedTournamentForPots?.teamsPerGroup || 4;
    const numGroups = pots[0]?.teams.length || 0;

    // Initialize groups
    const groups: {
      name: string;
      teams: { id: string; name: string; avgLevel: number }[];
    }[] = [];
    for (let i = 0; i < numGroups; i++) {
      groups.push({
        name: `Group ${String.fromCharCode(65 + i)}`,
        teams: [],
      });
    }

    setDrawGroups(groups);
    setCurrentDrawStep(0);
    setPotDialogOpen(false);
    setDrawAnimationOpen(true);
    setIsDrawing(true);
  };

  const performDrawStep = () => {
    if (!isDrawing) return;

    const numPots = pots.length;
    const numGroups = drawGroups.length;
    const totalSteps = numPots * numGroups;

    if (currentDrawStep >= totalSteps) {
      // Draw complete
      setIsDrawing(false);
      return;
    }

    // Determine which pot and which group
    const currentPot = Math.floor(currentDrawStep / numGroups);
    const currentGroup = currentDrawStep % numGroups;

    // Pick a random team from the current pot
    const availableTeams = pots[currentPot].teams.filter(
      (team) =>
        !drawGroups.some((group) => group.teams.some((t) => t.id === team.id)),
    );

    if (availableTeams.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableTeams.length);
      const selectedTeam = availableTeams[randomIndex];

      const newGroups = [...drawGroups];
      newGroups[currentGroup].teams.push(selectedTeam);
      setDrawGroups(newGroups);
    }

    setCurrentDrawStep(currentDrawStep + 1);
  };

  useEffect(() => {
    if (isDrawing && drawAnimationOpen) {
      const timer = setTimeout(() => {
        performDrawStep();
      }, 1500); // 1.5 seconds between each draw
      return () => clearTimeout(timer);
    }
  }, [currentDrawStep, isDrawing, drawAnimationOpen]);

  const handleDragStart = (
    team: { id: string; name: string; avgLevel: number },
    groupIdx: number,
  ) => {
    setDraggedTeam({ team, sourceGroupIdx: groupIdx });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetGroupIdx: number) => {
    if (!draggedTeam) return;

    const { team, sourceGroupIdx } = draggedTeam;

    // Don't do anything if dropping in the same group
    if (sourceGroupIdx === targetGroupIdx) {
      setDraggedTeam(null);
      return;
    }

    // Create new groups array with the team moved
    const newGroups = [...drawGroups];

    // Remove team from source group
    newGroups[sourceGroupIdx] = {
      ...newGroups[sourceGroupIdx],
      teams: newGroups[sourceGroupIdx].teams.filter((t) => t.id !== team.id),
    };

    // Add team to target group
    newGroups[targetGroupIdx] = {
      ...newGroups[targetGroupIdx],
      teams: [...newGroups[targetGroupIdx].teams, team],
    };

    setDrawGroups(newGroups);
    setDraggedTeam(null);
  };

  const finalizeGroups = async () => {
    try {
      // Convert draw groups to API format
      const groups = drawGroups.map((group) => ({
        id: group.name.toLowerCase().replace(" ", "-"),
        name: group.name,
        teamIds: group.teams.map((t) => t.id),
      }));

      await apiRequest(
        `/api/tournaments/${selectedTournamentForPots!.id}/generate-groups`,
        {
          method: "POST",
          body: JSON.stringify({ groups }),
        },
      );

      showSnackbar("Groups generated successfully!");
      setDrawAnimationOpen(false);
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to generate groups");
      console.error(err);
    }
  };

  const handleOpenStandingsDialog = (tournament: Tournament) => {
    setSelectedTournamentForStandings(tournament);
    setStandingsDialogOpen(true);
  };

  const handleOpenBracketDialog = async (tournament: Tournament) => {
    // Fetch fresh tournament data to ensure we have latest scores
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}`);
      const freshTournament = await response.json();
      setSelectedTournamentForBracket(freshTournament);
      setBracketDialogOpen(true);
    } catch (err) {
      console.error("Failed to fetch tournament:", err);
      // Fallback to using the tournament from the list
      setSelectedTournamentForBracket(tournament);
      setBracketDialogOpen(true);
    }
  };

  const getTeamName = (
    teamId: string | undefined,
    tournament?: Tournament,
  ): string => {
    if (!teamId) return "TBD";
    const tournamentToUse = tournament || selectedTournamentForBracket;
    if (!tournamentToUse) return teamId;
    const team = tournamentToUse.registeredTeams?.find((t) => t.id === teamId);
    if (!team) return `Team ${teamId.substring(0, 8)}`;

    // Prefer player names over teamName
    if (team.player1Name && team.player2Name) {
      return `${team.player1Name} / ${team.player2Name}`;
    }
    // Use teamName only if it doesn't look auto-generated and player names aren't available
    if (team.teamName && !team.teamName.startsWith("Team ")) {
      return team.teamName;
    }
    return `Team ${teamId.substring(0, 8)}`;
  };

  // Renders e.g. "1st" or, for a cancelled decider treated as a draw, "3rd/4th"
  const ordinalLabel = (position: number, span: number): string => {
    const suffix = (n: number) =>
      n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
    if (span <= 1) return `${position}${suffix(position)}`;
    return `${position}${suffix(position)}/${position + span - 1}${suffix(position + span - 1)}`;
  };

  // Helper to get brackets from either old or new structure
  const getMastersCupBracket = (tournament: Tournament | null) => {
    if (!tournament) return null;
    return (
      tournament.mastersCupBracket ||
      (tournament as any).knockoutBracket?.mastersCup ||
      null
    );
  };

  const getTourCupBracket = (tournament: Tournament | null) => {
    if (!tournament) return null;
    return (
      tournament.tourCupBracket ||
      (tournament as any).knockoutBracket?.tourCup ||
      null
    );
  };

  const getUniqueBracketRounds = (
    bracket: any[],
    includeConsolation = false,
  ) => {
    const rounds = Array.from(
      new Set(
        bracket
          .filter((m) =>
            includeConsolation ? m.isConsolationMatch : !m.isConsolationMatch,
          )
          .map((m) => m.round),
      ),
    );
    // Order: early play-in rounds first, then QF → SF → Final
    // Or: consolation-semi-finals, third-place, fifth-place, seventh-place for consolation
    const mainOrder = [
      "Round 1", "Round 2", "Round 3", "Round 4", "Round 5",
      "round-of-32",
      "round-of-16",
      "quarter-finals",
      "semi-finals",
      "final",
    ];
    const consolationOrder = [
      "consolation-semi-finals",
      "third-place",
      "fifth-place",
      "seventh-place",
    ];
    const order = includeConsolation ? consolationOrder : mainOrder;

    return rounds.sort((a, b) => {
      const aIdx = order.indexOf(a);
      const bIdx = order.indexOf(b);
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
  };

  const getRoundMatches = (bracket: any[], round: string) => {
    return bracket.filter((m) => m.round === round);
  };

  const handleOpenScoreDialog = (match: any, bracketType: string) => {
    if (match.winnerId) return; // Don't open if already scored
    if (!match.team1Id || !match.team2Id) return; // Don't open if teams not set
    setSelectedMatchForScoring({ ...match, bracketType });
    setTeam1Games(match.team1Games || 0);
    setTeam2Games(match.team2Games || 0);
    setScoreDialogOpen(true);
  };

  const handleScoreKnockoutMatch = async () => {
    if (!selectedMatchForScoring || !selectedTournamentForBracket) return;

    if (team1Games === 0 && team2Games === 0) {
      showSnackbar("Please enter game scores");
      return;
    }

    try {
      const payload = {
        matchId: selectedMatchForScoring.id,
        team1Games,
        team2Games,
        bracketType:
          selectedMatchForScoring.bracketType === "Masters Cup"
            ? "mastersCup"
            : "tourCup",
      };

      await apiRequest(
        `/api/tournaments/${selectedTournamentForBracket.id}/score-knockout-match`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      showSnackbar("Match scored successfully!");
      setScoreDialogOpen(false);
      setSelectedMatchForScoring(null);
      setTeam1Games(0);
      setTeam2Games(0);

      // Refresh tournament data - fetch fresh data to ensure we have latest updates
      const response = await apiRequest(
        `/api/tournaments/${selectedTournamentForBracket.id}`,
      );
      const updatedTournament = await response.json();

      console.log("Updated tournament after scoring:", updatedTournament);
      console.log(
        "Masters Cup bracket:",
        updatedTournament.mastersCupBracket ||
          updatedTournament.knockoutBracket?.mastersCup,
      );
      console.log(
        "Tour Cup bracket:",
        updatedTournament.tourCupBracket ||
          updatedTournament.knockoutBracket?.tourCup,
      );

      // Update the bracket dialog with fresh data
      setSelectedTournamentForBracket(updatedTournament);

      // Force re-render by incrementing refresh key
      setBracketRefreshKey((prev) => prev + 1);

      // Also refresh the tournaments list
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to score match");
      console.error(err);
    }
  };

  const renderMatchCard = (
    match: any,
    colorScheme: "success" | "warning" = "success",
    bracketType: string = "",
  ) => (
    <Card
      key={match.id}
      variant="outlined"
      sx={{
        bgcolor: match.winnerId ? "action.hover" : "background.paper",
        border: 2,
        borderColor: match.winnerId ? `${colorScheme}.main` : "divider",
        minWidth: 200,
        cursor:
          !match.winnerId && match.team1Id && match.team2Id
            ? "pointer"
            : "default",
        "&:hover":
          !match.winnerId && match.team1Id && match.team2Id
            ? {
                borderColor: `${colorScheme}.dark`,
                bgcolor: "action.hover",
              }
            : {},
      }}
      onClick={() => handleOpenScoreDialog(match, bracketType)}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          Match #{match.matchNumber + 1}
        </Typography>
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              bgcolor:
                match.winnerId === match.team1Id
                  ? `${colorScheme}.light`
                  : "background.default",
              borderRadius: 1,
              border: 1,
              borderColor:
                match.winnerId === match.team1Id
                  ? `${colorScheme}.main`
                  : "divider",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={match.winnerId === match.team1Id ? "bold" : "normal"}
              noWrap
              sx={{ flex: 1 }}
            >
              {getTeamName(match.team1Id)}
            </Typography>
            {match.team1Games !== undefined && match.team1Games !== null && (
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ ml: 1, minWidth: 24, textAlign: "center" }}
              >
                {match.team1Games}
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            textAlign="center"
            color="text.secondary"
          >
            vs
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              bgcolor:
                match.winnerId === match.team2Id
                  ? `${colorScheme}.light`
                  : "background.default",
              borderRadius: 1,
              border: 1,
              borderColor:
                match.winnerId === match.team2Id
                  ? `${colorScheme}.main`
                  : "divider",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={match.winnerId === match.team2Id ? "bold" : "normal"}
              noWrap
              sx={{ flex: 1 }}
            >
              {getTeamName(match.team2Id)}
            </Typography>
            {match.team2Games !== undefined && match.team2Games !== null && (
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ ml: 1, minWidth: 24, textAlign: "center" }}
              >
                {match.team2Games}
              </Typography>
            )}
          </Box>
        </Stack>
        {match.winnerId && (
          <Chip
            label="✓ Complete"
            color={colorScheme}
            size="small"
            sx={{ mt: 1, width: "100%" }}
          />
        )}
        {!match.winnerId && match.team1Id && match.team2Id && (
          <Chip label="Pending" size="small" sx={{ mt: 1, width: "100%" }} />
        )}
      </CardContent>
    </Card>
  );

  const renderMirrorBracket = (
    bracket: any[],
    title: string,
    color: string,
    isConsolation = false,
    bracketType: string = "",
  ) => {
    const rounds = getUniqueBracketRounds(bracket, isConsolation);
    const colorScheme = isConsolation ? "warning" : "success";

    // For consolation brackets with finals (single match rounds), show them linearly
    if (isConsolation) {
      return (
        <Box sx={{ overflowX: "auto", pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 4,
              minWidth: "max-content",
              alignItems: "flex-start",
            }}
          >
            {rounds.map((round) => {
              const roundMatches = getRoundMatches(bracket, round);
              if (roundMatches.length === 0) return null;

              return (
                <Box key={round} sx={{ minWidth: 250 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    textAlign="center"
                    mb={2}
                    textTransform="capitalize"
                  >
                    {round.replace(/-/g, " ")}
                  </Typography>
                  <Stack spacing={3}>
                    {roundMatches.map((match) =>
                      renderMatchCard(match, colorScheme, bracketType),
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    // For main bracket, split the bracket into top and bottom halves
    // Top half flows left to right, bottom half flows right to left, final in center
    const finalRound = rounds[rounds.length - 1];
    const finalMatch = getRoundMatches(bracket, finalRound)[0];

    // Build left side (top half) and right side (bottom half)
    const leftSide = [];
    const rightSide = [];

    for (let i = 0; i < rounds.length - 1; i++) {
      // Exclude final
      const round = rounds[i];
      const roundMatches = getRoundMatches(bracket, round);
      const midpoint = Math.ceil(roundMatches.length / 2);

      leftSide.push({
        round,
        matches: roundMatches.slice(0, midpoint),
      });

      rightSide.push({
        round,
        matches: roundMatches.slice(midpoint),
      });
    }

    return (
      <Box sx={{ overflowX: "auto", pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 4,
            minWidth: "max-content",
            alignItems: "center",
          }}
        >
          {/* Left side - flows left to right */}
          {leftSide.map((roundData, idx) => (
            <Box key={`left-${roundData.round}`} sx={{ minWidth: 250 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                textAlign="center"
                mb={2}
                textTransform="capitalize"
              >
                {roundData.round.replace(/-/g, " ")}
              </Typography>
              <Stack spacing={3}>
                {roundData.matches.map((match) =>
                  renderMatchCard(match, colorScheme, bracketType),
                )}
              </Stack>
            </Box>
          ))}

          {/* Final in the center */}
          {finalMatch && (
            <Box sx={{ minWidth: 250 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                textAlign="center"
                mb={2}
                textTransform="capitalize"
              >
                {finalRound.replace(/-/g, " ")}
              </Typography>
              {renderMatchCard(finalMatch, colorScheme, bracketType)}
            </Box>
          )}

          {/* Right side - reverse order to flow right to left */}
          {rightSide
            .slice()
            .reverse()
            .map((roundData, idx) => (
              <Box key={`right-${roundData.round}`} sx={{ minWidth: 250 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  textAlign="center"
                  mb={2}
                  textTransform="capitalize"
                >
                  {roundData.round.replace(/-/g, " ")}
                </Typography>
                <Stack spacing={3}>
                  {roundData.matches.map((match) =>
                    renderMatchCard(match, colorScheme, bracketType),
                  )}
                </Stack>
              </Box>
            ))}
        </Box>
      </Box>
    );
  };

  const renderTreeBracket = (
    bracket: any[],
    title: string,
    color: string,
    isConsolation = false,
    bracketType: string = "",
  ) => {
    const rounds = getUniqueBracketRounds(bracket, isConsolation);
    const colorScheme = isConsolation ? "warning" : "success";
    const totalRounds = rounds.length;

    // Split bracket into top and bottom halves
    const finalRound = rounds[rounds.length - 1];
    const finalMatch = getRoundMatches(bracket, finalRound)[0];

    return (
      <Box sx={{ overflowX: "auto", pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            minWidth: "max-content",
            p: 2,
          }}
        >
          {/* Tree structure with final at top/center */}
          <Box sx={{ display: "flex", gap: 6 }}>
            {rounds
              .slice()
              .reverse()
              .map((round, idx) => {
                const roundMatches = getRoundMatches(bracket, round);
                if (roundMatches.length === 0) return null; // Skip empty rounds

                const isRoot = idx === 0; // Final is first in reversed order

                return (
                  <Box
                    key={round}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      textTransform="capitalize"
                      color="text.secondary"
                    >
                      {round.replace(/-/g, " ")}
                    </Typography>
                    <Stack spacing={isRoot ? 0 : 4}>
                      {roundMatches.map((match) =>
                        renderMatchCard(match, colorScheme, bracketType),
                      )}
                    </Stack>
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
    );
  };

  const getAllGroupMatches = (tournament: Tournament) => {
    if (!tournament?.groups) return [];

    const allMatches: any[] = [];

    tournament.groups.forEach((group) => {
      const matches = generateRoundRobinMatches(group.teamIds);
      matches.forEach((match) => {
        // Check if this specific match has been scored
        const isScored =
          group.matchResults?.some(
            (m) =>
              (m.team1Id === match.team1Id && m.team2Id === match.team2Id) ||
              (m.team1Id === match.team2Id && m.team2Id === match.team1Id),
          ) || false;

        allMatches.push({
          groupId: group.id,
          groupName: group.name,
          ...match,
          isScored,
        });
      });
    });

    return allMatches;
  };

  const getInitializedStandings = (group: any) => {
    // If standings exist, return them
    if (group.standings && group.standings.length > 0) {
      return [...group.standings].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
        return b.gamesWon - a.gamesWon;
      });
    }

    // Otherwise, initialize standings with 0 values for all teams in group
    return group.teamIds.map((teamId: string) => ({
      teamId,
      matchesPlayed: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      gamesWon: 0,
      gamesLost: 0,
      gamesDiff: 0,
      points: 0,
    }));
  };

  const generateRoundRobinMatches = (teamIds: string[]) => {
    const matches: { team1Id: string; team2Id: string }[] = [];
    for (let i = 0; i < teamIds.length; i++) {
      for (let j = i + 1; j < teamIds.length; j++) {
        matches.push({ team1Id: teamIds[i], team2Id: teamIds[j] });
      }
    }
    return matches;
  };

  const areAllGroupMatchesComplete = (
    tournament: Tournament | null,
  ): boolean => {
    if (!tournament || !tournament.groups || tournament.groups.length === 0)
      return false;

    // Check if all groups have standings data
    const allGroupsHaveStandings = tournament.groups.every(
      (group) => group.standings && group.standings.length > 0,
    );

    if (!allGroupsHaveStandings) return false;

    // Calculate expected matches per group
    // In a round-robin, each team plays every other team once
    // Total matches = n * (n-1) / 2 where n is number of teams
    const allMatchesComplete = tournament.groups.every((group) => {
      const numTeams = group.teamIds.length;
      const expectedMatches = (numTeams * (numTeams - 1)) / 2;

      // Sum of all matches played by teams in this group
      // Each match counts for 2 teams, so total matchesPlayed / 2 = actual matches
      const totalMatchesPlayed =
        group.standings?.reduce(
          (sum, standing) => sum + standing.matchesPlayed,
          0,
        ) || 0;
      const actualMatches = totalMatchesPlayed / 2;

      return actualMatches >= expectedMatches;
    });

    return allMatchesComplete;
  };

  const handleGenerateKnockout = async (tournament: Tournament) => {
    if (!areAllGroupMatchesComplete(tournament)) {
      showSnackbar(
        "All group matches must be completed before generating knockout bracket",
      );
      return;
    }

    try {
      const response = await apiRequest(
        `/api/tournaments/${tournament.id}/generate-knockout`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate knockout bracket");
      }

      const result = await response.json();
      console.log("Knockout generated:", result);

      showSnackbar("Knockout bracket generated!");

      // Wait a moment for the database to update, then fetch
      setTimeout(async () => {
        await fetchTournaments();
      }, 500);
    } catch (err: any) {
      showSnackbar(err.message || "Failed to generate knockout");
      console.error("Error generating knockout:", err);
    }
  };

  const handleOpenRankingsDialog = async (tournament: Tournament) => {
    try {
      const response = await apiRequest(
        `/api/tournaments/${tournament.id}/rankings`,
      );
      const rankings = await response.json();
      setTournamentRankings(rankings);
      setSelectedTournamentForRankings(tournament);
      setRankingsDialogOpen(true);
    } catch (err) {
      console.error("Failed to fetch rankings:", err);
      showSnackbar("Failed to load rankings");
    }
  };

  const handleCompleteTournament = async (tournament: Tournament) => {
    try {
      const response = await apiRequest(
        `/api/tournaments/${tournament.id}/complete`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to complete tournament");
      }

      showSnackbar("Tournament marked as completed!");
      await fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to complete tournament");
      console.error("Error completing tournament:", err);
    }
  };

  const areAllKnockoutMatchesScored = (tournament: Tournament): boolean => {
    const mastersCup =
      tournament.knockoutBracket?.mastersCup || tournament.mastersCupBracket;
    const tourCup =
      tournament.knockoutBracket?.tourCup || tournament.tourCupBracket;

    if (!mastersCup && !tourCup) {
      return false;
    }

    const allMatches = [...(mastersCup || []), ...(tourCup || [])];
    return allMatches.every(
      (match) =>
        (match.winnerId !== undefined && match.winnerId !== null) ||
        !!match.isCancelled,
    );
  };

  const handleOpenDisplayModeDialog = (tournament: Tournament) => {
    setSelectedTournamentForDisplay(tournament);
    setDisplayModeDialogOpen(true);
  };

  const handleOpenDeleteDialog = (tournament: Tournament) => {
    setSelectedTournamentForDelete(tournament);
    setKeepScores(true); // Default to keeping scores
    setDeleteDialogOpen(true);
  };

  const handleOpenScheduleDialog = (tournament: Tournament) => {
    setSelectedTournamentForSchedule(tournament);
    setScheduleDialogOpen(true);
  };

  const handleScheduleDialogUpdate = async () => {
    fetchTournaments();
    if (selectedTournamentForSchedule?.id) {
      try {
        const resp = await apiRequest(`/api/tournaments/${selectedTournamentForSchedule.id}`);
        const updated = await resp.json();
        setSelectedTournamentForSchedule(updated);
      } catch (e) {
        console.error("Failed to refresh schedule tournament:", e);
      }
    }
  };

  const handleDeleteTournament = async () => {
    if (!selectedTournamentForDelete) return;

    setIsDeleting(true);
    try {
      await apiRequest(
        `/api/tournaments/${selectedTournamentForDelete.id}?keepScores=${keepScores}`,
        {
          method: "DELETE",
        },
      );

      showSnackbar(
        keepScores
          ? "Tournament deleted. Player scores have been preserved."
          : "Tournament deleted. All scores have been removed from player records.",
      );
      setDeleteDialogOpen(false);
      setSelectedTournamentForDelete(null);
      fetchTournaments();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to delete tournament");
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenLiveDisplay = (mode: "slideshow" | "scroll" | "tabs") => {
    if (selectedTournamentForDisplay) {
      if (mode === "slideshow") {
        // Open slide time selector for slideshow mode
        setDisplayModeDialogOpen(false);
        setSlideTimeDialogOpen(true);
      } else {
        window.open(
          `/display/${selectedTournamentForDisplay.id}?mode=${mode}`,
          "_blank",
        );
        setDisplayModeDialogOpen(false);
      }
    }
  };

  const handleOpenSlideshow = () => {
    if (selectedTournamentForDisplay) {
      const enabledSlides = SLIDE_DEFS
        .filter(s => slideConfig[s.key]?.enabled)
        .map(s => `${s.key}:${slideConfig[s.key].duration}`)
        .join(",");
      if (!enabledSlides) return; // nothing selected
      window.open(
        `/display/${selectedTournamentForDisplay.id}?mode=slideshow&slides=${enabledSlides}`,
        "_blank",
      );
      setSlideTimeDialogOpen(false);
      setSlideConfig(defaultSlideConfig());
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setVenue("");
    setTournamentLevels(["Open"]);
    setTournamentSeasonId(selectedSeason?.id || null); // Reset to currently selected season
    setFormat("groups-knockout");
    setScoringSystem("sets-games");
    setGroupMatchFormat("best-of-7");
    setKnockoutMatchFormat("best-of-7");
    setTimedMatchDuration(20);
    setChangeoverTime(0);
    setNumberOfTeams(16);
    setTeamsPerGroup(4);
    setKnockoutFormatType("single-bracket");
    setMastersCupAdvanceCount(2);
    setTourCupAdvanceCount(2);
    setKnockoutAdvanceCount(2);
    setAdvancedAdvancementEnabled(false);
    setCrossGroupRules([]);
    setPlayAllPositions(true);
    setIsPointsRelevant(true);
    setRegistrationOpenDate("");
    setRegistrationCloseDate("");
    setStartDate("");
    setStartTime("09:00");
    setEndDate("");
    setNumberOfCourts(4);
    setCourtPricePerSlot("");
    setRegistrationFeePerTeam("");
    setEvents([]);
    setVisibilityMode("all");
    setVisibleToPlayerIds([]);
    setPlayerRegistrationManagedByAdmin(false);
    setPosterImageUrl("");
    setPosterPreview("");
    setIsEditMode(false);
    setEditingTournamentId(null);
    setCustomSeeding({});
  };

  const handleOpenChecklist = (tournament: Tournament) => {
    setChecklistTournament(tournament);
    setChecklist(tournament.playerChecklist || {});
    setChecklistSearch("");
    setChecklistDialogOpen(true);
  };

  const handleChecklistToggle = async (
    playerId: string,
    field: "showed" | "paid",
    value: boolean,
    tournament: Tournament,
  ) => {
    const current = checklist[playerId] || { showed: false, paid: false };
    const updated = {
      ...checklist,
      [playerId]: { ...current, [field]: value },
    };
    setChecklist(updated);
    setChecklistSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ playerChecklist: updated }),
      });
      // Update the in-memory tournament so re-opening the dialog keeps the state
      setTournaments((prev) =>
        prev.map((t) => t.id === tournament.id ? { ...t, playerChecklist: updated } : t),
      );
      if (checklistTournament?.id === tournament.id) {
        setChecklistTournament((prev) => prev ? { ...prev, playerChecklist: updated } : prev);
      }
    } catch {
      // Revert on error
      setChecklist(checklist);
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleOpenOverview = async (tournament: Tournament) => {
    setOverviewTournament(tournament);
    setOverviewData(null);
    setOverviewLoading(true);
    setOverviewDialogOpen(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournament.id}/registrations-overview`);
      if (res.ok) setOverviewData(await res.json());
    } catch { /* ignore */ }
    finally { setOverviewLoading(false); }
  };

  const handleToggleAutoApprove = async (checked: boolean) => {
    setAutoApproveLoading(true);
    try {
      await apiRequest("/api/tournament-settings/registration-config", {
        method: "PUT",
        body: JSON.stringify({ autoApproveTournamentAccounts: checked }),
      });
      setAutoApproveTournament(checked);
    } catch {
      showSnackbar("Failed to save setting");
    } finally {
      setAutoApproveLoading(false);
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCopyShareLink = (tournament: Tournament) => {
    const link = `${window.location.origin}/join/${tournament.id}`;
    navigator.clipboard.writeText(link).then(
      () => showSnackbar("Share link copied to clipboard!"),
      () => showSnackbar(`Share link: ${link}`),
    );
  };

  const getStatusColor = (
    status: TournamentStatus,
  ): "default" | "primary" | "success" | "warning" | "error" => {
    switch (status) {
      case "upcoming":
        return "default";
      case "registration":
        return "primary";
      case "group-stage":
        return "primary";
      case "knockout":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: TournamentStatus): string => {
    return status
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const toggleDescriptionExpansion = (tournamentId: string) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tournamentId)) {
        newSet.delete(tournamentId);
      } else {
        newSet.add(tournamentId);
      }
      return newSet;
    });
  };

  const shouldTruncateDescription = (description: string) => {
    return description.length > 150;
  };

  const getTruncatedDescription = (description: string) => {
    if (description.length <= 150) return description;
    return description.substring(0, 150) + "...";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading tournaments...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        <TrophyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Tournaments
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          aria-label="tournament tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 100, sm: 160 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        >
          <Tab 
            label={<Box sx={{ display: { xs: 'none', sm: 'block' } }}>Tournaments</Box>} 
            icon={<TrophyIcon />} 
            iconPosition="start" 
            sx={{ '& .MuiTab-iconWrapper': { display: { xs: 'inline-flex', sm: 'inline-flex' } } }}
          />
          <Tab
            label={<Box>Points {' '}<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Configuration</Box></Box>}
            icon={<SettingsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setTournamentSeasonId(selectedSeason?.id || null); // Pre-fill with current filter season
              setDialogOpen(true);
            }}
            size="small"
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Tournament
          </Button>
        </Box>

        <Box sx={{ mb: 3, display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <TournamentLevelSelector
            value={selectedLevel}
            onChange={setSelectedLevel}
            fullWidth
          />
          <SeasonSelector allowAllSeasons />
        </Box>

        {/* Auto-approve tournament accounts toggle */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoApproveTournament}
                onChange={(e) => handleToggleAutoApprove(e.target.checked)}
                disabled={autoApproveLoading}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Auto-approve tournament account registrations
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When on, players who sign up via the tournament registration page get instant access (no admin review needed). Their accounts are flagged as <strong>Tournament Account</strong>.
                </Typography>
              </Box>
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {tournaments.length === 0 ? (
          <Alert severity="info">
            No tournaments found. Create your first tournament to get started!
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {tournaments.map((tournament) => {
              const isAmericana = tournament.format === "americana";
              const registered = isAmericana
                ? (tournament.americanaPlayerIds?.length || 0)
                : (tournament.registeredTeams?.length || 0);
              const registrationLabel = isAmericana ? "Players registered" : "Teams registered";
              const registrationPct = Math.min(100, (registered / (tournament.maxTeams || 1)) * 100);
              const posterUrl = (tournament as any).posterImageUrl;
              const sponsors = (tournament as any).sponsors || [];

              return (
              <Grid item xs={12} sm={6} lg={4} key={tournament.id} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    display: 'flex', flexDirection: 'column', width: '100%',
                    overflow: 'hidden', borderRadius: isPE ? '16px' : 2,
                    boxShadow: isPE ? 'none' : 3,
                    ...(isPE && { border: '1px solid #D8C4C1' }),
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: isPE ? 'none' : 6, ...(isPE && { borderColor: '#B79498' }) },
                  }}
                >
                  {/* ── Poster header ── */}
                  <Box
                    sx={{
                      height: 160,
                      position: 'relative',
                      backgroundImage: posterUrl
                        ? `url(${posterUrl})`
                        : isPE ? 'none' : 'linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)',
                      ...(isPE && { backgroundColor: '#743444' }),
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    {/* Status + level chips */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={getStatusLabel(tournament.status)} color={isPE ? 'default' : getStatusColor(tournament.status)} size="small"
                        sx={{ fontWeight: 700, ...(isPE && (tournament.status === 'completed'
                          ? { bgcolor: '#74586C', color: '#fff' }
                          : { bgcolor: '#F4C48B', color: '#743444' })) }} />
                      {getTournamentLevels(tournament).map((lvl) => (
                        <Chip key={lvl} label={lvl} size="small" sx={{ bgcolor: isPE ? '#fff' : 'rgba(255,255,255,0.85)', color: isPE ? '#743444' : undefined, fontWeight: 600 }} />
                      ))}
                      {isPE && getLevelRangeLabel(getTournamentLevels(tournament)) && (
                        <Chip label={getLevelRangeLabel(getTournamentLevels(tournament))} size="small" sx={{ bgcolor: '#E49421', color: '#fff', fontWeight: 600 }} />
                      )}
                      {!tournament.isPointsRelevant && (
                        <Chip label="One-off" size="small" variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }} />
                      )}
                    </Box>

                    {/* Admin icon actions */}
                    <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleCopyShareLink(tournament)} title="Copy share link"
                        sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
                        <ShareIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      {tournament.status !== "completed" && tournament.status !== "cancelled" && (
                        <IconButton size="small" onClick={() => handleEditTournament(tournament)} title="Edit"
                          sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => handleOpenDeleteDialog(tournament)} title="Delete"
                        sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#ffcdd2', '&:hover': { bgcolor: 'rgba(211,47,47,0.7)' } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    {/* Title bar at the bottom of poster */}
                    <Box
                      sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: isPE ? 'rgba(116,52,68,0.88)' : 'linear-gradient(transparent, rgba(0,0,0,0.75))',
                        px: 1.5, pt: 2, pb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, textShadow: isPE ? 'none' : '0 1px 3px rgba(0,0,0,0.5)' }}>
                        {tournament.name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Card body ── */}
                  <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2 }}>
                    {/* Date & venue */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        📅 {new Date(tournament.startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      {tournament.venue && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          component="a"
                          href={getMapsUrl(tournament.venue)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: "underline" }}
                        >📍 {tournament.venue}</Typography>
                      )}
                    </Box>

                    {/* Teams progress */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{registrationLabel}</Typography>
                        <Typography variant="caption" fontWeight={700}>{registered} / {tournament.maxTeams}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={registrationPct}
                        sx={{ height: 5, borderRadius: 3, bgcolor: isPE ? '#D8C4C1' : 'action.hover', ...(isPE && { '& .MuiLinearProgress-bar': { bgcolor: '#E49421' } }) }}
                        color={registrationPct >= 100 ? "success" : "primary"}
                      />
                    </Box>

                    {/* Sponsors */}
                    {sponsors.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {sponsors.map((s: any) => (
                          <Box key={s.name} component="img" src={s.logoUrl} alt={s.name}
                            sx={{ height: 22, objectFit: 'contain', borderRadius: 0.5 }} />
                        ))}
                      </Box>
                    )}

                    {/* Phase label */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mt: 0.5 }}>
                      {{
                        upcoming: "📋 Preparation",
                        registration: "📋 Registration open",
                        "group-stage": "🎯 Group Stage",
                        knockout: "🏆 Knockout",
                        completed: "✅ Completed",
                        cancelled: "❌ Cancelled",
                      }[tournament.status] ?? tournament.status}
                    </Typography>
                  </CardContent>

                  {/* ── Go to Tournament button ── */}
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(
                        tournament.format === "americana"
                          ? `/admin/tournaments/${tournament.id}/americana`
                          : `/admin/tournaments/${tournament.id}`
                      )}
                      sx={{ fontWeight: 700, ...(isPE && { bgcolor: '#E49421', color: '#fff', borderRadius: 999, '&:hover': { bgcolor: '#74586C' } }) }}
                    >
                      Go to Tournament
                    </Button>
                  </Box>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        )}

        {/* Create/Edit Tournament Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            if (isEditMode) resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditMode ? "Edit Tournament" : "Create New Tournament"}
          </DialogTitle>
          <DialogContent>
            {isEditMode ? (
              <Tabs
                value={activeStep}
                onChange={(_, v) => setActiveStep(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mt: 1, borderBottom: 1, borderColor: "divider" }}
              >
                {TOURNAMENT_FORM_STEPS.slice(0, 5).map((label) => (
                  <Tab key={label} label={label} />
                ))}
              </Tabs>
            ) : (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, display: { xs: "block", md: "none" } }}>
                  Step {activeStep + 1} of {TOURNAMENT_FORM_STEPS.length} — {TOURNAMENT_FORM_STEPS[activeStep]}
                </Typography>
                <Stepper nonLinear activeStep={activeStep} sx={{ mt: 1, display: { xs: "none", md: "flex" } }}>
                  {TOURNAMENT_FORM_STEPS.map((label, i) => (
                    <Step key={label} completed={i < activeStep}>
                      <StepButton disabled={i > activeStep} onClick={() => setActiveStep(i)}>
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </>
            )}
            <Box sx={{ mt: 3 }}>
              <Stack spacing={3} sx={{ display: activeStep === 0 ? "flex" : "none" }}>
              <TextField
                label="Tournament Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />

              <RichTextEditor
                label="Description"
                value={description}
                onChange={setDescription}
                placeholder="Enter tournament description"
              />

              <TextField
                label="Venue"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                fullWidth
                placeholder="e.g., Central Padel Club, Court 1-4"
                helperText={
                  venue.trim() ? (
                    <a href={getMapsUrl(venue)} target="_blank" rel="noopener noreferrer">
                      📍 Open in Google Maps
                    </a>
                  ) : (
                    "Add a venue to get a Google Maps link"
                  )
                }
              />

              <FormControl fullWidth>
                <InputLabel>Tournament Levels</InputLabel>
                <Select
                  multiple
                  value={tournamentLevels}
                  label="Tournament Levels"
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = (typeof v === "string" ? v.split(",") : v) as TournamentLevel[];
                    if (next.length > 0) setTournamentLevels(next); // keep at least one level
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as TournamentLevel[]).map((lvl) => (
                        <Chip key={lvl} label={lvl} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {TOURNAMENT_LEVELS.map((lvl) => (
                    <MenuItem key={lvl} value={lvl}>
                      <Checkbox checked={tournamentLevels.includes(lvl)} />
                      <ListItemText primary={isPE && getLevelRangeLabel([lvl]) ? `${lvl}: ${getLevelRangeLabel([lvl])}` : lvl} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Season</InputLabel>
                <Select
                  value={tournamentSeasonId || "none"}
                  label="Season"
                  onChange={(e) =>
                    setTournamentSeasonId(
                      e.target.value === "none" ? null : e.target.value,
                    )
                  }
                >
                  <MenuItem value="none">
                    <em>No Season (General)</em>
                  </MenuItem>
                  {allSeasons.map((season) => (
                    <MenuItem key={season.id} value={season.id}>
                      {season.name} {season.isActive && "(Active)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

                {/* Poster image upload */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Tournament Poster
                  </Typography>
                  {posterPreview && (
                    <Box
                      sx={{
                        width: "100%",
                        height: 160,
                        borderRadius: 1,
                        mb: 1,
                        backgroundImage: `url(${posterPreview})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "1px solid",
                        borderColor: "divider",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => { setPosterImageUrl(""); setPosterPreview(""); }}
                        sx={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.5)", color: "white", "&:hover": { background: "rgba(0,0,0,0.7)" } }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  )}
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    disabled={uploadingPoster}
                    startIcon={uploadingPoster ? <CircularProgress size={16} /> : undefined}
                    fullWidth
                  >
                    {uploadingPoster ? "Uploading…" : posterPreview ? "Change Poster" : "Upload Poster (16:9, max 5 MB)"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setPosterPreview(URL.createObjectURL(file));
                        setUploadingPoster(true);
                        try {
                          const url = await ImageUploadService.uploadTournamentPoster(file);
                          setPosterImageUrl(url);
                          setPosterPreview(url);
                        } catch (err: any) {
                          showSnackbar(err?.message || "Failed to upload poster");
                          setPosterPreview("");
                        } finally {
                          setUploadingPoster(false);
                        }
                      }}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Used as the card background on the public tournaments page.
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={3} sx={{ display: activeStep === 1 ? "flex" : "none" }}>
              <TextField
                label="Number of Teams"
                type="number"
                value={numberOfTeams}
                onChange={(e) => setNumberOfTeams(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                required
                InputProps={{ inputProps: { min: 2 } }}
                error={format === "groups-knockout" && typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && numberOfTeams % teamsPerGroup !== 0}
                helperText={
                  format === "groups-knockout" && typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && numberOfTeams % teamsPerGroup !== 0
                    ? `${numberOfTeams} teams cannot be evenly divided into groups of ${teamsPerGroup}. Try ${Math.floor(numberOfTeams / teamsPerGroup) * teamsPerGroup} or ${Math.ceil(numberOfTeams / teamsPerGroup) * teamsPerGroup} teams.`
                    : format === "groups-knockout" && typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number'
                    ? `This will create ${numberOfTeams / teamsPerGroup} groups`
                    : undefined
                }
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={format}
                      label="Format"
                      onChange={(e) =>
                        setFormat(e.target.value as TournamentFormat)
                      }
                    >
                      <MenuItem value="groups-knockout">
                        Groups + Knockout
                      </MenuItem>
                      <MenuItem value="single-elimination">
                        Single Elimination
                      </MenuItem>
                      <MenuItem value="double-elimination">
                        Double Elimination
                      </MenuItem>
                      <MenuItem value="round-robin">Round Robin</MenuItem>
                      <MenuItem value="americana">Americana (Fixed Rounds)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scoring System</InputLabel>
                    <Select
                      value={scoringSystem}
                      label="Scoring System"
                      onChange={(e) =>
                        setScoringSystem(e.target.value as MatchScoringSystem)
                      }
                    >
                      <MenuItem value="sets-games">
                        Sets & Games (Traditional)
                      </MenuItem>
                      <MenuItem value="timed-points">Timed Points</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {scoringSystem === "sets-games" && (
                <Grid container spacing={2}>
                  {/* Group stage format — hidden for pure knockout formats; reused as the match format for Americana */}
                  {(format === "groups-knockout" || format === "round-robin" || format === "americana") && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{format === "americana" ? "Match Format" : "Group Stage Format"}</InputLabel>
                        <Select
                          value={groupMatchFormat}
                          label={format === "americana" ? "Match Format" : "Group Stage Format"}
                          onChange={(e) => setGroupMatchFormat(e.target.value as TournamentMatchFormat)}
                        >
                          <MenuItem value="best-of-7">Best of 7 Games (1 set, first to 4)</MenuItem>
                          <MenuItem value="best-of-9">Best of 9 Games (1 set, first to 5)</MenuItem>
                          <MenuItem value="best-of-11">Best of 11 Games (1 set, first to 6)</MenuItem>
                          <MenuItem value="best-of-3">Best of 3 Sets</MenuItem>
                          <MenuItem value="best-of-5">Best of 5 Sets</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {/* Knockout format — hidden for round-robin and americana (no bracket stage) */}
                  {format !== "round-robin" && format !== "americana" && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Knockout Stage Format</InputLabel>
                        <Select
                          value={knockoutMatchFormat}
                          label="Knockout Stage Format"
                          onChange={(e) => setKnockoutMatchFormat(e.target.value as TournamentMatchFormat)}
                        >
                          <MenuItem value="best-of-7">Best of 7 Games (1 set, first to 4)</MenuItem>
                          <MenuItem value="best-of-9">Best of 9 Games (1 set, first to 5)</MenuItem>
                          <MenuItem value="best-of-11">Best of 11 Games (1 set, first to 6)</MenuItem>
                          <MenuItem value="best-of-3">Best of 3 Sets</MenuItem>
                          <MenuItem value="best-of-5">Best of 5 Sets</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              )}

              {format === "groups-knockout" && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Teams Per Group"
                      type="number"
                      value={teamsPerGroup}
                      onChange={(e) => setTeamsPerGroup(e.target.value === "" ? "" : Number(e.target.value))}
                      fullWidth
                      required
                      InputProps={{ inputProps: { min: 2, max: 6 } }}
                      error={typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && numberOfTeams % teamsPerGroup !== 0}
                      helperText={
                        typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && numberOfTeams % teamsPerGroup !== 0
                          ? `${numberOfTeams} teams cannot be evenly divided into groups of ${teamsPerGroup}`
                          : typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number'
                          ? `This will create ${numberOfTeams / teamsPerGroup} groups with ${teamsPerGroup} teams each`
                          : "Number of teams in each group"
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Knockout Format</InputLabel>
                      <Select
                        value={knockoutFormatType}
                        label="Knockout Format"
                        onChange={(e) =>
                          setKnockoutFormatType(
                            e.target.value as KnockoutFormatType,
                          )
                        }
                      >
                        <MenuItem value="single-bracket">
                          Standard Single Bracket (Single Elimination)
                        </MenuItem>
                        <MenuItem value="double-bracket">
                          Double Bracket ({bracketNames.primary} & {bracketNames.secondary})
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {knockoutFormatType === "double-bracket" && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={`${bracketNames.primary} Advance`}
                          type="number"
                          value={mastersCupAdvanceCount}
                          onChange={(e) =>
                            setMastersCupAdvanceCount(e.target.value === "" ? "" : Number(e.target.value))
                          }
                          fullWidth
                          required
                          InputProps={{
                            inputProps: { min: 1, max: typeof teamsPerGroup === 'number' ? teamsPerGroup - 1 : undefined },
                          }}
                          error={
                            typeof teamsPerGroup === 'number' && 
                            typeof mastersCupAdvanceCount === 'number' && 
                            typeof tourCupAdvanceCount === 'number' && 
                            (mastersCupAdvanceCount + tourCupAdvanceCount > teamsPerGroup || mastersCupAdvanceCount >= teamsPerGroup)
                          }
                          helperText={
                            typeof teamsPerGroup === 'number' && 
                            typeof mastersCupAdvanceCount === 'number' && 
                            typeof tourCupAdvanceCount === 'number' && 
                            mastersCupAdvanceCount + tourCupAdvanceCount > teamsPerGroup
                              ? `${bracketNames.primary} (${mastersCupAdvanceCount}) + ${bracketNames.secondary} (${tourCupAdvanceCount}) = ${mastersCupAdvanceCount + tourCupAdvanceCount} exceeds ${teamsPerGroup} teams per group`
                              : `Top teams to ${bracketNames.primary} (winners bracket)`
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={`${bracketNames.secondary} Advance`}
                          type="number"
                          value={tourCupAdvanceCount}
                          onChange={(e) =>
                            setTourCupAdvanceCount(e.target.value === "" ? "" : Number(e.target.value))
                          }
                          fullWidth
                          required
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: (typeof teamsPerGroup === 'number' && typeof mastersCupAdvanceCount === 'number') ? teamsPerGroup - mastersCupAdvanceCount : undefined,
                            },
                          }}
                          error={
                            typeof teamsPerGroup === 'number' && 
                            typeof mastersCupAdvanceCount === 'number' && 
                            typeof tourCupAdvanceCount === 'number' && 
                            mastersCupAdvanceCount + tourCupAdvanceCount > teamsPerGroup
                          }
                          helperText={
                            typeof teamsPerGroup === 'number' && 
                            typeof mastersCupAdvanceCount === 'number' && 
                            typeof tourCupAdvanceCount === 'number' && 
                            mastersCupAdvanceCount + tourCupAdvanceCount > teamsPerGroup
                              ? `${bracketNames.primary} (${mastersCupAdvanceCount}) + ${bracketNames.secondary} (${tourCupAdvanceCount}) = ${mastersCupAdvanceCount + tourCupAdvanceCount} exceeds ${teamsPerGroup} teams per group`
                              : `Next teams to ${bracketNames.secondary} (losers bracket)`
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={advancedAdvancementEnabled}
                              onChange={(e) => setAdvancedAdvancementEnabled(e.target.checked)}
                            />
                          }
                          label="Advanced: add cross-group rules (e.g. best runner-up across all groups)"
                        />
                      </Grid>

                      {advancedAdvancementEnabled && (
                        <Grid item xs={12}>
                          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {bracketNames.primary} = every group's top {mastersCupAdvanceCount} place(s) plus any rules below. {bracketNames.secondary} gets everyone else.
                            </Typography>
                            {crossGroupRules.map((rule, index) => (
                              <Stack direction="row" spacing={1} alignItems="center" key={index} sx={{ mb: 1 }}>
                                <Typography variant="body2">Best</Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={rule.count}
                                  onChange={(e) => {
                                    const count = e.target.value === "" ? 1 : Number(e.target.value);
                                    setCrossGroupRules(rules => rules.map((r, i) => i === index ? { ...r, count } : r));
                                  }}
                                  InputProps={{ inputProps: { min: 1 } }}
                                  sx={{ width: 80 }}
                                />
                                <Typography variant="body2">team(s) at position</Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={rule.position + 1}
                                  onChange={(e) => {
                                    const position = (e.target.value === "" ? 1 : Number(e.target.value)) - 1;
                                    setCrossGroupRules(rules => rules.map((r, i) => i === index ? { ...r, position } : r));
                                  }}
                                  InputProps={{ inputProps: { min: 1, max: typeof teamsPerGroup === 'number' ? teamsPerGroup : undefined } }}
                                  sx={{ width: 80 }}
                                />
                                <Typography variant="body2">across all groups → {bracketNames.primary}</Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => setCrossGroupRules(rules => rules.filter((_, i) => i !== index))}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              size="small"
                              onClick={() => setCrossGroupRules(rules => [...rules, { position: 1, count: 1 }])}
                            >
                              Add rule
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}

                  {knockoutFormatType === "single-bracket" && (
                    <Grid item xs={12}>
                      <TextField
                        label="Teams Advancing Per Group"
                        type="number"
                        value={knockoutAdvanceCount}
                        onChange={(e) =>
                          setKnockoutAdvanceCount(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        fullWidth
                        required
                        InputProps={{
                          inputProps: { min: 1, max: typeof teamsPerGroup === 'number' ? teamsPerGroup - 1 : undefined },
                        }}
                        error={
                          typeof teamsPerGroup === 'number' && 
                          typeof knockoutAdvanceCount === 'number' && 
                          knockoutAdvanceCount >= teamsPerGroup
                        }
                        helperText={
                          typeof teamsPerGroup === 'number' && 
                          typeof knockoutAdvanceCount === 'number' && 
                          knockoutAdvanceCount >= teamsPerGroup
                            ? `Cannot advance ${knockoutAdvanceCount} teams from groups of ${teamsPerGroup}. Max is ${teamsPerGroup - 1}.`
                            : "How many teams advance from each group to knockout stage"
                        }
                      />
                    </Grid>
                  )}
                  
                  {/* Preview Knockout Seeding Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<BracketIcon />}
                      onClick={() => setSeedingPreviewOpen(true)}
                      disabled={(typeof numberOfTeams === 'number' ? numberOfTeams : 0) < 4 || (typeof teamsPerGroup === 'number' ? teamsPerGroup : 0) < 2}
                      sx={{ py: 1.5 }}
                    >
                      Preview Knockout Stage Matchups
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: "center" }}>
                      See which group positions will face each other in knockout stage
                    </Typography>
                  </Grid>
                </Grid>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={playAllPositions}
                    onChange={(e) => setPlayAllPositions(e.target.checked)}
                  />
                }
                label="Play All Positions (3rd place, 5th place, etc.)"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPointsRelevant}
                    onChange={(e) => setIsPointsRelevant(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Points Relevant</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Winners earn Road to Masters points. Uncheck for one-off tournaments that should not affect the standings.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 1 }}
              />
              </Stack>
              <Stack spacing={3} sx={{ display: activeStep === 2 ? "flex" : "none" }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Tournament Start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TimeField24
                    label="Start Time"
                    value={startTime}
                    onChange={setStartTime}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Match scheduling starts at this time"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Tournament End (optional)"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Number of Courts"
                type="number"
                value={numberOfCourts}
                onChange={(e) => setNumberOfCourts(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                InputProps={{ inputProps: { min: 1, max: 20 } }}
                helperText="How many courts available for parallel matches"
              />

              <TextField
                label="Match Duration (minutes)"
                type="number"
                value={timedMatchDuration}
                onChange={(e) =>
                  setTimedMatchDuration(e.target.value === "" ? "" : Number(e.target.value))
                }
                fullWidth
                helperText={scoringSystem === "sets-games" ? "Used for schedule planning" : "Match ends when time is up"}
                InputProps={{ inputProps: { min: 5, max: 180 } }}
              />

              <TextField
                label="Changeover Time (minutes)"
                type="number"
                value={changeoverTime}
                onChange={(e) => setChangeoverTime(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 30 } }}
                helperText="Time between matches on the same court (0 = no gap)"
              />

              {/* Events/Breaks Section */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Events & Breaks
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingEvent(null);
                      setEventName("");
                      setEventType("break");
                      setEventStartTime("");
                      setEventDuration(15);
                      setEventDescription("");
                      setEventDialogOpen(true);
                    }}
                  >
                    Add Event
                  </Button>
                </Box>
                {events.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No events or breaks added. Add breaks, ceremonies, or other time slots.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {events.sort((a, b) => a.startTime.localeCompare(b.startTime)).map((event) => (
                      <Card key={event.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {event.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {event.type.charAt(0).toUpperCase() + event.type.slice(1)} • {event.startTime} • {event.duration} min
                            </Typography>
                            {event.description && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {event.description}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingEvent(event);
                                setEventName(event.name);
                                setEventType(event.type);
                                setEventStartTime(event.startTime);
                                setEventDuration(event.duration);
                                setEventDescription(event.description || "");
                                setEventDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setEvents(events.filter(e => e.id !== event.id));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
              </Stack>
              <Stack spacing={3} sx={{ display: activeStep === 3 ? "flex" : "none" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={playerRegistrationManagedByAdmin}
                      onChange={(e) => setPlayerRegistrationManagedByAdmin(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Registration Managed By Admin Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, players can still view the tournament (based on visibility settings) but cannot self-register.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 1 }}
                />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Registration Opens"
                    type="datetime-local"
                    value={registrationOpenDate}
                    onChange={(e) => setRegistrationOpenDate(e.target.value)}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Registration Closes"
                    type="datetime-local"
                    value={registrationCloseDate}
                    onChange={(e) => setRegistrationCloseDate(e.target.value)}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Court price per 90-min slot (€)"
                type="number"
                value={courtPricePerSlot}
                onChange={(e) => setCourtPricePerSlot(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                helperText="Used to calculate court cost in the schedule profit preview"
                sx={{ mt: 2 }}
              />

              <TextField
                label="Registration fee per team (€)"
                type="number"
                value={registrationFeePerTeam}
                onChange={(e) => setRegistrationFeePerTeam(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                helperText="Used to calculate revenue in the schedule profit preview"
                sx={{ mt: 2 }}
              />
              </Stack>
              <Stack spacing={3} sx={{ display: activeStep === 4 ? "flex" : "none" }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 0.5, fontSize: "0.875rem" }}>
                    Player Visibility
                  </FormLabel>
                  <RadioGroup
                    value={visibilityMode}
                    onChange={(e) => {
                      setVisibilityMode(e.target.value as "all" | "specific" | "hidden");
                      if (e.target.value !== "specific") setVisibleToPlayerIds([]);
                    }}
                  >
                    <FormControlLabel value="all" control={<Radio size="small" />} label="Visible to all players" />
                    <FormControlLabel value="specific" control={<Radio size="small" />} label="Visible to specific players only" />
                    <FormControlLabel value="hidden" control={<Radio size="small" />} label="Hidden from all players" />
                  </RadioGroup>
                </FormControl>

                {visibilityMode === "specific" && (
                  <Autocomplete
                    multiple
                    options={players}
                    getOptionLabel={(p) => `${p.name} (${p.division})`}
                    value={players.filter((p) => visibleToPlayerIds.includes(p.id))}
                    onChange={(_, selected) => setVisibleToPlayerIds(selected.map((p) => p.id))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Players who can see this tournament"
                        size="small"
                        placeholder="Search players…"
                        sx={{ mt: 1 }}
                      />
                    )}
                    renderTags={(selected, getTagProps) =>
                      selected.map((p, i) => (
                        <Chip {...getTagProps({ index: i })} key={p.id} label={p.name} size="small" />
                      ))
                    }
                  />
                )}

                {/* Sponsors */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Sponsors</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSponsors(prev => [...prev, { name: "", logoUrl: "", websiteUrl: "", uploading: false }])}
                    >
                      + Add Sponsor
                    </Button>
                  </Box>

                  {sponsors.map((sponsor, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                    >
                      {/* Logo preview / upload */}
                      <Box sx={{ flexShrink: 0 }}>
                        {sponsor.logoUrl ? (
                          <Box
                            component="img"
                            src={sponsor.logoUrl}
                            alt={sponsor.name}
                            sx={{ width: 72, height: 40, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "grey.100" }}
                          />
                        ) : (
                          <Box sx={{ width: 72, height: 40, bgcolor: "grey.100", borderRadius: 1, border: "1px dashed", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="text.secondary">Logo</Typography>
                          </Box>
                        )}
                        <Button
                          component="label"
                          size="small"
                          variant="text"
                          disabled={sponsor.uploading}
                          sx={{ mt: 0.5, fontSize: "0.65rem", p: 0.5, minWidth: 72 }}
                        >
                          {sponsor.uploading ? "Uploading…" : sponsor.logoUrl ? "Change" : "Upload"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setSponsors(prev => prev.map((s, i) => i === idx ? { ...s, uploading: true } : s));
                              try {
                                const url = await ImageUploadService.uploadSponsorLogo(file, `sponsor-${Date.now()}`);
                                setSponsors(prev => prev.map((s, i) => i === idx ? { ...s, logoUrl: url, uploading: false } : s));
                              } catch {
                                setSponsors(prev => prev.map((s, i) => i === idx ? { ...s, uploading: false } : s));
                              }
                            }}
                          />
                        </Button>
                      </Box>

                      {/* Name + website */}
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        <TextField
                          size="small"
                          label="Sponsor name"
                          value={sponsor.name}
                          onChange={(e) => setSponsors(prev => prev.map((s, i) => i === idx ? { ...s, name: e.target.value } : s))}
                        />
                        <TextField
                          size="small"
                          label="Website (optional)"
                          placeholder="https://sponsor.com"
                          value={sponsor.websiteUrl}
                          onChange={(e) => setSponsors(prev => prev.map((s, i) => i === idx ? { ...s, websiteUrl: e.target.value } : s))}
                        />
                      </Box>

                      {/* Remove */}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setSponsors(prev => prev.filter((_, i) => i !== idx))}
                        sx={{ mt: 0.5 }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  ))}

                  {sponsors.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No sponsors yet. Click "+ Add Sponsor" to add one.
                    </Typography>
                  )}
                </Box>
              </Stack>
              {!isEditMode && (
              <Stack spacing={2} sx={{ display: activeStep === 5 ? "flex" : "none" }}>
                {(() => {
                  const seasonName = allSeasons.find((se) => se.id === tournamentSeasonId)?.name || "No Season (General)";
                  const rows: [string, string][] = [
                    ["Name", name || "—"],
                    ["Venue", venue || "—"],
                    ["Level", tournamentLevels.join(", ") || "—"],
                    ["Season", seasonName],
                    ["Format", String(format)],
                    ["Scoring system", String(scoringSystem)],
                    ["Number of teams", String(numberOfTeams)],
                    ...(format === "groups-knockout" ? ([["Teams per group", String(teamsPerGroup)], ["Knockout format", String(knockoutFormatType)]] as [string, string][]) : []),
                    ["Play all positions", playAllPositions ? "Yes" : "No"],
                    ["Points relevant", isPointsRelevant ? "Yes" : "No"],
                    ["Tournament start", startDate ? `${startDate}${startTime ? " " + startTime : ""}` : "—"],
                    ["Tournament end", endDate || "—"],
                    ["Courts", String(numberOfCourts)],
                    ["Match duration", timedMatchDuration === "" ? "—" : `${timedMatchDuration} min`],
                    ["Changeover", changeoverTime === "" ? "—" : `${changeoverTime} min`],
                    ["Events & breaks", String(events.length)],
                    ["Registration opens", registrationOpenDate || "—"],
                    ["Registration closes", registrationCloseDate || "—"],
                    ["Registration fee / team", registrationFeePerTeam === "" ? "—" : `€${registrationFeePerTeam}`],
                    ["Court price / slot", courtPricePerSlot === "" ? "—" : `€${courtPricePerSlot}`],
                    ["Registration managed by admin", playerRegistrationManagedByAdmin ? "Yes" : "No"],
                    ["Visibility", visibilityMode === "all" ? "All players" : visibilityMode === "specific" ? `${visibleToPlayerIds.length} specific players` : "Hidden"],
                    ["Poster", posterImageUrl ? "Uploaded" : "None"],
                    ["Sponsors", String(sponsors.filter((sp) => sp.logoUrl).length)],
                  ];
                  const missing = !name || !registrationOpenDate || !registrationCloseDate || !startDate;
                  return (
                    <>
                      {missing && (
                        <Alert severity="warning">
                          Some required fields are empty (name, registration dates or start date). Go back to fill them in.
                        </Alert>
                      )}
                      <Grid container spacing={1}>
                        {rows.map(([label, value]) => (
                          <React.Fragment key={label}>
                            <Grid item xs={5}>
                              <Typography variant="body2" color="text.secondary">{label}</Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2" fontWeight={600}>{value}</Typography>
                            </Grid>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </>
                  );
                })()}
              </Stack>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDialogOpen(false);
                if (isEditMode) resetForm();
              }}
            >
              Cancel
            </Button>
            {!isEditMode && activeStep > 0 && (
              <Button onClick={() => setActiveStep((st) => st - 1)}>Back</Button>
            )}
            {!isEditMode && activeStep < TOURNAMENT_FORM_STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  const err = validateTournamentStep(activeStep);
                  if (err) {
                    showSnackbar(err);
                    return;
                  }
                  if (activeStep === 2) {
                    // datetime-local inputs only emit a value once date AND time are complete,
                    // so prefill complete defaults the user can adjust.
                    const pad = (n: number) => String(n).padStart(2, "0");
                    if (!registrationOpenDate) {
                      const d = new Date();
                      setRegistrationOpenDate(
                        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
                      );
                    }
                    if (!registrationCloseDate && startDate) {
                      setRegistrationCloseDate(`${startDate}T${startTime || "09:00"}`);
                    }
                  }
                  setActiveStep((st) => st + 1);
                }}
              >
                Next
              </Button>
            ) : (
              <Button onClick={handleCreateTournament} variant="contained">
                {isEditMode ? "Update Tournament" : "Create Tournament"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Event/Break Dialog */}
        <Dialog
          open={eventDialogOpen}
          onClose={() => setEventDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                label="Event Name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Lunch Break, Awards Ceremony"
              />

              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventType}
                  label="Event Type"
                  onChange={(e) => setEventType(e.target.value as TournamentEventType)}
                >
                  <MenuItem value="break">Break</MenuItem>
                  <MenuItem value="ceremony">Ceremony</MenuItem>
                  <MenuItem value="tombola">Tombola</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TimeField24
                label="Start Time"
                value={eventStartTime}
                onChange={setEventStartTime}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="When does this event start?"
              />

              <TextField
                label="Duration (minutes)"
                type="number"
                value={eventDuration}
                onChange={(e) => setEventDuration(e.target.value === "" ? "" : Number(e.target.value))}
                fullWidth
                InputProps={{ inputProps: { min: 5, max: 180 } }}
              />

              <TextField
                label="Description (optional)"
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Additional details about this event"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!eventName.trim()) {
                  showSnackbar("Please enter an event name");
                  return;
                }
                if (!eventStartTime) {
                  showSnackbar("Please enter a complete start time (hours and minutes)");
                  return;
                }

                const newEvent: TournamentEvent = {
                  id: editingEvent?.id || `event_${Date.now()}`,
                  name: eventName,
                  type: eventType,
                  startTime: eventStartTime,
                  duration: typeof eventDuration === 'number' ? eventDuration : 15,
                  description: eventDescription || undefined,
                };

                if (editingEvent) {
                  // Update existing event
                  setEvents(events.map(e => e.id === editingEvent.id ? newEvent : e));
                } else {
                  // Add new event
                  setEvents([...events, newEvent]);
                }

                setEventDialogOpen(false);
              }}
              variant="contained"
            >
              {editingEvent ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Register Team Dialog */}
        <Dialog
          open={registerDialogOpen}
          onClose={() => setRegisterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Register Team</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select two players to form a team for this tournament
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<ShuffleIcon />}
                  onClick={handleRandomTeamSelection}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Random Team
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<GroupIcon />}
                  onClick={() => handleGenerateRandomTeams(24)}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Generate 24 Teams
                </Button>
                {(() => {
                  const spotsLeft =
                    (selectedTournamentForReg?.maxTeams || 0) -
                    (selectedTournamentForReg?.registeredTeams?.length || 0);
                  const testPlayersAvailable = players.filter(
                    (p) =>
                      isTestPlayerName(p.name, currentOrg?.slug) &&
                      !selectedTournamentForReg?.registeredTeams?.some(
                        (t) => t.player1Id === p.id || t.player2Id === p.id
                      )
                  ).length;
                  if (spotsLeft <= 0 || testPlayersAvailable < 2) return null;
                  return (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<GroupIcon />}
                      onClick={handleFillWithTestTeams}
                      sx={{ flex: 1, minWidth: 140 }}
                    >
                      Fill {spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} (test players)
                    </Button>
                  );
                })()}
              </Box>

              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Or manually select
                </Typography>
              </Divider>

              <FormControl fullWidth required>
                <InputLabel>Player 1</InputLabel>
                <Select
                  value={player1Id}
                  label="Player 1"
                  onChange={(e) => setPlayer1Id(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select player</em>
                  </MenuItem>
                  {players.map((player) => {
                    const isRegistered =
                      selectedTournamentForReg?.registeredTeams?.some(
                        (team) =>
                          team.player1Id === player.id ||
                          team.player2Id === player.id,
                      );
                    return (
                      <MenuItem
                        key={player.id}
                        value={player.id}
                        disabled={player.id === player2Id || isRegistered}
                      >
                        {player.name}{" "}
                        {isRegistered ? "(Already registered)" : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Player 2</InputLabel>
                <Select
                  value={player2Id}
                  label="Player 2"
                  onChange={(e) => setPlayer2Id(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select player</em>
                  </MenuItem>
                  {players.map((player) => {
                    const isRegistered =
                      selectedTournamentForReg?.registeredTeams?.some(
                        (team) =>
                          team.player1Id === player.id ||
                          team.player2Id === player.id,
                      );
                    return (
                      <MenuItem
                        key={player.id}
                        value={player.id}
                        disabled={player.id === player1Id || isRegistered}
                      >
                        {player.name}{" "}
                        {isRegistered ? "(Already registered)" : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                label="Team Name (optional)"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                fullWidth
                placeholder="e.g., The Smashers"
                helperText="Leave empty to auto-generate from player names"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegisterDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRegisterTeam}
              variant="contained"
              disabled={!player1Id || !player2Id}
            >
              Register Team
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Tournament Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !isDeleting && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Tournament</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>
                  Are you sure you want to delete <strong>{selectedTournamentForDelete?.name}</strong>?
                </Typography>
                <Typography variant="body2">
                  This action cannot be undone. All tournament data including groups, brackets, and matches will be permanently deleted.
                </Typography>
              </Alert>

              <Divider />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepScores}
                      onChange={(e) => setKeepScores(e.target.checked)}
                      disabled={isDeleting}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2">
                        Keep player scores and statistics
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {keepScores
                          ? "Player points, wins, losses, and rankings will be preserved. The tournament will be removed but its impact on player records remains."
                          : "All scores from this tournament will be removed from player records. Points, wins, losses, and rankings will be recalculated as if the tournament never happened."}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", ml: 0 }}
                />
              </Box>

              {!keepScores && (
                <Alert severity="error" icon={<DeleteIcon />}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> Removing scores will affect player rankings and statistics across the entire season. This may impact promotion/relegation decisions.
                  </Typography>
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTournament}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Tournament"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Registered Teams Dialog */}
        <Dialog
          open={viewTeamsDialogOpen}
          onClose={() => setViewTeamsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Registered Teams - {selectedTournamentForView?.name}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedTournamentForView?.registeredTeams?.length || 0} /{" "}
                {selectedTournamentForView?.maxTeams} teams registered
              </Typography>

              {selectedTournamentForView?.registeredTeams &&
              selectedTournamentForView.registeredTeams.length > 0 ? (
                <List>
                  {selectedTournamentForView.registeredTeams.map(
                    (team, idx) => {
                      const player1 = players.find(
                        (p) => p.id === team.player1Id,
                      );
                      const player2 = players.find(
                        (p) => p.id === team.player2Id,
                      );

                      // Use names from team object first, fall back to player lookup
                      const player1Name =
                        team.player1Name || player1?.name || "Unknown";
                      const player2Name =
                        team.player2Name || player2?.name || "Unknown";

                      const displayName =
                        team.teamName || `${player1Name} & ${player2Name}`;

                      // Calculate average level
                      const level1 = player1?.level || 0;
                      const level2 = player2?.level || 0;
                      const averageLevel = ((level1 + level2) / 2).toFixed(1);
                      const hasLevels =
                        player1?.level !== undefined &&
                        player2?.level !== undefined;

                      return (
                        <ListItem
                          key={team.id}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {idx + 1}. {displayName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {player1Name} & {player2Name}
                          </Typography>
                          {player1 && player2 && (
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Levels: {player1.level || "N/A"} /{" "}
                                {player2.level || "N/A"}
                              </Typography>
                              {hasLevels && (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  fontWeight="bold"
                                >
                                  Average: {averageLevel}
                                </Typography>
                              )}
                            </>
                          )}
                        </ListItem>
                      );
                    },
                  )}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No teams registered yet
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewTeamsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Pot Management Dialog */}
        <Dialog
          open={potDialogOpen}
          onClose={() => setPotDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Group Draw - Pot Management</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Alert severity="info">
                Teams are divided into pots based on average level. Pot 1
                contains the strongest teams. You can move teams between pots
                before starting the draw.
              </Alert>

              <Grid container spacing={2}>
                {pots.map((pot) => (
                  <Grid
                    item
                    xs={12}
                    md={pots.length === 3 ? 4 : 3}
                    key={pot.potNumber}
                  >
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Pot {pot.potNumber}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense>
                          {pot.teams.map((team, idx) => (
                            <ListItem
                              key={team.id}
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                mb: 1,
                                flexDirection: "column",
                                alignItems: "flex-start",
                                p: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {team.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Avg Level: {team.avgLevel.toFixed(1)}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mt: 1 }}
                              >
                                {pot.potNumber > 1 && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      moveTeamBetweenPots(
                                        team.id,
                                        pot.potNumber,
                                        pot.potNumber - 1,
                                      )
                                    }
                                  >
                                    ↑ Pot {pot.potNumber - 1}
                                  </Button>
                                )}
                                {pot.potNumber < pots.length && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      moveTeamBetweenPots(
                                        team.id,
                                        pot.potNumber,
                                        pot.potNumber + 1,
                                      )
                                    }
                                  >
                                    ↓ Pot {pot.potNumber + 1}
                                  </Button>
                                )}
                              </Stack>
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPotDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={startDraw}
              variant="contained"
              startIcon={<TrophyIcon />}
            >
              Start Draw Animation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Draw Animation Dialog */}
        <Dialog
          open={drawAnimationOpen}
          onClose={() => !isDrawing && setDrawAnimationOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Group Stage Draw</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {isDrawing && (
                <Alert severity="info">
                  Drawing teams... Step {currentDrawStep + 1} of{" "}
                  {pots.length * drawGroups.length}
                </Alert>
              )}
              {!isDrawing && (
                <Alert severity="success">
                  Draw complete! Review the groups and click "Finalize" to save.
                </Alert>
              )}

              {!isDrawing && (
                <Alert severity="info">
                  💡 You can drag and drop teams between groups to adjust the
                  draw before finalizing.
                </Alert>
              )}

              <Grid container spacing={2}>
                {drawGroups.map((group, idx) => (
                  <Grid
                    item
                    xs={12}
                    md={drawGroups.length <= 4 ? 6 : 4}
                    key={idx}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        bgcolor: "background.default",
                        border:
                          draggedTeam && !isDrawing
                            ? "2px dashed"
                            : "1px solid",
                        borderColor:
                          draggedTeam && !isDrawing
                            ? "primary.main"
                            : "divider",
                      }}
                      onDragOver={!isDrawing ? handleDragOver : undefined}
                      onDrop={!isDrawing ? () => handleDrop(idx) : undefined}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          {group.name}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense>
                          {group.teams.map((team, teamIdx) => (
                            <ListItem
                              key={team.id}
                              draggable={!isDrawing}
                              onDragStart={
                                !isDrawing
                                  ? () => handleDragStart(team, idx)
                                  : undefined
                              }
                              sx={{
                                border: 1,
                                borderColor: "primary.main",
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: "background.paper",
                                animation: "fadeIn 0.5s ease-in",
                                cursor: !isDrawing ? "grab" : "default",
                                "&:active": {
                                  cursor: !isDrawing ? "grabbing" : "default",
                                },
                                opacity:
                                  draggedTeam?.team.id === team.id ? 0.5 : 1,
                                "@keyframes fadeIn": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateY(-10px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                              }}
                            >
                              <ListItemText
                                primary={team.name}
                                secondary={`Avg Level: ${team.avgLevel.toFixed(1)} • From Pot ${Math.floor(teamIdx) + 1}`}
                                primaryTypographyProps={{ fontWeight: "bold" }}
                              />
                            </ListItem>
                          ))}
                          {group.teams.length === 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                            >
                              {isDrawing
                                ? "Waiting for draw..."
                                : "Drop team here"}
                            </Typography>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {isDrawing && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <LinearProgress sx={{ width: "50%" }} />
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDrawAnimationOpen(false)}
              disabled={isDrawing}
            >
              Cancel
            </Button>
            <Button
              onClick={finalizeGroups}
              variant="contained"
              disabled={isDrawing}
            >
              Finalize Groups
            </Button>
          </DialogActions>
        </Dialog>

        {/* Group Standings Dialog */}
        <Dialog
          open={standingsDialogOpen}
          onClose={() => setStandingsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Group Standings - {selectedTournamentForStandings?.name}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {selectedTournamentForStandings?.groups &&
              selectedTournamentForStandings.groups.length > 0 ? (
                <Grid container spacing={2}>
                  {selectedTournamentForStandings.groups.map((group) => (
                    <Grid
                      item
                      xs={12}
                      md={
                        selectedTournamentForStandings.groups!.length <= 2
                          ? 6
                          : 4
                      }
                      key={group.id}
                    >
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">
                            {group.name}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />

                          <Box sx={{ overflowX: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead>
                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    #
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "left",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Team
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    P
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    W
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    D
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    L
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    GW
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    GL
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    G+/-
                                  </th>
                                  <th
                                    style={{
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Pts
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getInitializedStandings(group).map(
                                  (standing: any, idx: number) => {
                                    const team =
                                      selectedTournamentForStandings!.registeredTeams?.find(
                                        (t: any) => t.id === standing.teamId,
                                      );
                                    const player1 = players.find(
                                      (p: Player) => p.id === team?.player1Id,
                                    );
                                    const player2 = players.find(
                                      (p: Player) => p.id === team?.player2Id,
                                    );

                                    // Use names from team object first, fall back to player lookup
                                    const player1Name =
                                      team?.player1Name ||
                                      player1?.name ||
                                      "P1";
                                    const player2Name =
                                      team?.player2Name ||
                                      player2?.name ||
                                      "P2";
                                    const teamName =
                                      team?.teamName ||
                                      `${player1Name} & ${player2Name}`;

                                    return (
                                      <tr
                                        key={standing.teamId}
                                        style={{
                                          borderBottom: "1px solid #e0e0e0",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "8px",
                                            fontWeight: "bold",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {idx + 1}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {teamName}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.matchesPlayed}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.wins}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.draws}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.losses}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.gamesWon}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.gamesLost}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.gamesDiff > 0 ? "+" : ""}
                                          {standing.gamesDiff}
                                        </td>
                                        <td
                                          style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontWeight: "bold",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {standing.points}
                                        </td>
                                      </tr>
                                    );
                                  },
                                )}
                              </tbody>
                            </table>
                          </Box>

                          <Divider sx={{ my: 2 }} />
                          <Typography variant="caption" color="text.secondary">
                            P: Matches Played • W: Wins • D: Draws • L: Losses •
                            GW: Games Won • GL: Games Lost • G+/-: Games
                            Difference • Pts: Points
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  No groups available
                </Typography>
              )}

              {selectedTournamentForStandings &&
                !areAllGroupMatchesComplete(selectedTournamentForStandings) && (
                  <Alert severity="warning">
                    Not all group matches have been completed yet. Complete all
                    matches to generate the knockout bracket.
                  </Alert>
                )}

              {selectedTournamentForStandings &&
                areAllGroupMatchesComplete(selectedTournamentForStandings) && (
                  <Alert severity="success">
                    All group matches completed! You can now generate the
                    knockout bracket.
                  </Alert>
                )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStandingsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Knockout Bracket Dialog */}
        <Dialog
          open={bracketDialogOpen}
          onClose={() => setBracketDialogOpen(false)}
          maxWidth="xl"
          fullWidth
          key={`bracket-${selectedTournamentForBracket?.id}-${bracketRefreshKey}`}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h5">
                Knockout Brackets - {selectedTournamentForBracket?.name}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <ToggleButtonGroup
                  value={bracketViewMode}
                  exclusive
                  onChange={(e, newMode) =>
                    newMode && setBracketViewMode(newMode)
                  }
                  size="small"
                >
                  <ToggleButton value="mirror">
                    <ViewStreamIcon sx={{ mr: 1 }} />
                    Mirror
                  </ToggleButton>
                  <ToggleButton value="tree">
                    <TreeIcon sx={{ mr: 1 }} />
                    Tree
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setBracketDialogOpen(false);
                    navigate(
                      `/admin/tournaments/${selectedTournamentForBracket?.id}?tab=scores`,
                    );
                  }}
                >
                  Score Matches
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={4} sx={{ mt: 2 }}>
              {/* Masters Cup Bracket */}
              {(() => {
                const mastersCupBracket = getMastersCupBracket(
                  selectedTournamentForBracket,
                );
                return (
                  mastersCupBracket &&
                  mastersCupBracket.length > 0 && (
                    <Box>
                      <Typography
                        variant="h5"
                        gutterBottom
                        color="primary"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TrophyIcon /> {bracketNames.primary}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      {/* Main Bracket */}
                      {bracketViewMode === "mirror"
                        ? renderMirrorBracket(
                            mastersCupBracket,
                            "Masters Cup",
                            "success",
                            false,
                            "Masters Cup",
                          )
                        : renderTreeBracket(
                            mastersCupBracket,
                            "Masters Cup",
                            "success",
                            false,
                            "Masters Cup",
                          )}

                      {/* Masters Cup Consolation Bracket */}
                      {selectedTournamentForBracket?.playAllPositions &&
                        mastersCupBracket.some(
                          (m: any) => m.isConsolationMatch,
                        ) &&
                        (bracketViewMode === "mirror"
                          ? renderMirrorBracket(
                              mastersCupBracket,
                              "Position Playoffs",
                              "warning",
                              true,
                              "Masters Cup",
                            )
                          : renderTreeBracket(
                              mastersCupBracket,
                              "Position Playoffs",
                              "warning",
                              true,
                              "Masters Cup",
                            ))}
                    </Box>
                  )
                );
              })()}

              {/* Tour Cup Bracket */}
              {(() => {
                const tourCupBracket = getTourCupBracket(
                  selectedTournamentForBracket,
                );
                return (
                  tourCupBracket &&
                  tourCupBracket.length > 0 && (
                    <Box>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          color: "#1976d2",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TrophyIcon /> {bracketNames.secondary}
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      {/* Main Bracket */}
                      {bracketViewMode === "mirror"
                        ? renderMirrorBracket(
                            tourCupBracket,
                            "Tour Cup",
                            "primary",
                            false,
                            "Tour Cup",
                          )
                        : renderTreeBracket(
                            tourCupBracket,
                            "Tour Cup",
                            "primary",
                            false,
                            "Tour Cup",
                          )}

                      {/* Tour Cup Consolation Bracket */}
                      {selectedTournamentForBracket?.playAllPositions &&
                        tourCupBracket.some((m: any) => m.isConsolationMatch) &&
                        (bracketViewMode === "mirror"
                          ? renderMirrorBracket(
                              tourCupBracket,
                              "Position Playoffs",
                              "warning",
                              true,
                              "Tour Cup",
                            )
                          : renderTreeBracket(
                              tourCupBracket,
                              "Position Playoffs",
                              "warning",
                              true,
                              "Tour Cup",
                            ))}
                    </Box>
                  )
                );
              })()}

              {!getMastersCupBracket(selectedTournamentForBracket) &&
                !getTourCupBracket(selectedTournamentForBracket) && (
                  <Alert severity="info">
                    No knockout brackets generated yet.
                  </Alert>
                )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBracketDialogOpen(false)}>Close</Button>
            {(getMastersCupBracket(selectedTournamentForBracket) ||
              getTourCupBracket(selectedTournamentForBracket)) && (
              <Button
                color="warning"
                onClick={async () => {
                  if (!selectedTournamentForBracket) return;

                  const bracketType = getMastersCupBracket(
                    selectedTournamentForBracket,
                  )
                    ? "mastersCup"
                    : "tourCup";

                  if (
                    !window.confirm(
                      `Are you sure you want to regenerate the ${bracketType === "mastersCup" ? bracketNames.primary : bracketNames.secondary} bracket? This will clear all match scores!`,
                    )
                  ) {
                    return;
                  }

                  try {
                    await apiRequest(
                      `/tournaments/${selectedTournamentForBracket.id}/regenerate-bracket`,
                      {
                        method: "POST",
                        body: JSON.stringify({ bracketType }),
                        headers: {
                          "Content-Type": "application/json",
                        },
                      },
                    );

                    // Refresh tournament data
                    const response = await apiRequest(
                      `/tournaments/${selectedTournamentForBracket.id}`,
                    );
                    const updatedTournament = await response.json();
                    setSelectedTournamentForBracket(updatedTournament);
                    setBracketRefreshKey((prev) => prev + 1);

                    alert("Bracket regenerated successfully!");
                  } catch (error) {
                    console.error("Error regenerating bracket:", error);
                    alert("Failed to regenerate bracket");
                  }
                }}
              >
                Regenerate Bracket
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setBracketDialogOpen(false);
                navigate(
                  `/admin/tournaments/${selectedTournamentForBracket?.id}?tab=scores`,
                );
              }}
            >
              Go to Scoring
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rankings Dialog */}
        <Dialog
          open={rankingsDialogOpen}
          onClose={() => setRankingsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrophyIcon color="primary" />
              <Typography variant="h5">
                Final Rankings - {selectedTournamentForRankings?.name}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={4} sx={{ mt: 2 }}>
              {tournamentRankings?.mastersCup && (
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: "#FFD700",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <TrophyIcon /> {bracketNames.primary}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((position) => {
                      const teamIds: string[] | undefined =
                        tournamentRankings.mastersCup[position];
                      if (!teamIds || teamIds.length === 0) return null;

                      const medal =
                        position === 1
                          ? "🥇"
                          : position === 2
                            ? "🥈"
                            : position === 3
                              ? "🥉"
                              : "";

                      return (
                        <Card
                          key={position}
                          sx={{
                            bgcolor:
                              position <= 3
                                ? "action.hover"
                                : "background.paper",
                            border:
                              position === 1
                                ? "2px solid #FFD700"
                                : position === 2
                                  ? "2px solid #C0C0C0"
                                  : position === 3
                                    ? "2px solid #CD7F32"
                                    : "none",
                          }}
                        >
                          <CardContent
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ minWidth: 64, fontWeight: "bold" }}
                            >
                              {medal} {ordinalLabel(position, teamIds.length)}
                            </Typography>
                            <Typography variant="body1">
                              {selectedTournamentForRankings &&
                                teamIds
                                  .map((teamId) =>
                                    getTeamName(
                                      teamId,
                                      selectedTournamentForRankings,
                                    ),
                                  )
                                  .join("  /  ")}
                              {teamIds.length > 1 && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  (tied — match cancelled)
                                </Typography>
                              )}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {tournamentRankings?.tourCup && (
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: "#1976d2",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <TrophyIcon /> {bracketNames.secondary}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((position) => {
                      const teamIds: string[] | undefined =
                        tournamentRankings.tourCup[position];
                      if (!teamIds || teamIds.length === 0) return null;

                      const medal =
                        position === 1
                          ? "🥇"
                          : position === 2
                            ? "🥈"
                            : position === 3
                              ? "🥉"
                              : "";

                      return (
                        <Card
                          key={position}
                          sx={{
                            bgcolor:
                              position <= 3
                                ? "action.hover"
                                : "background.paper",
                            border:
                              position === 1
                                ? "2px solid #FFD700"
                                : position === 2
                                  ? "2px solid #C0C0C0"
                                  : position === 3
                                    ? "2px solid #CD7F32"
                                    : "none",
                          }}
                        >
                          <CardContent
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ minWidth: 64, fontWeight: "bold" }}
                            >
                              {medal} {ordinalLabel(position, teamIds.length)}
                            </Typography>
                            <Typography variant="body1">
                              {selectedTournamentForRankings &&
                                teamIds
                                  .map((teamId) =>
                                    getTeamName(
                                      teamId,
                                      selectedTournamentForRankings,
                                    ),
                                  )
                                  .join("  /  ")}
                              {teamIds.length > 1 && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  (tied — match cancelled)
                                </Typography>
                              )}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRankingsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Display Mode Selector Dialog */}
        <Dialog
          open={displayModeDialogOpen}
          onClose={() => setDisplayModeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Display Mode for Live View</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Card
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => handleOpenLiveDisplay("slideshow")}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📽️ Auto-Cycling Slideshow
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically cycles through all sections. Choose custom
                    timing in the next step. Perfect for unattended TV displays.
                  </Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => handleOpenLiveDisplay("scroll")}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📜 Single Scrolling Page
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All information visible on one long page. Scroll to see
                    different sections.
                  </Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => handleOpenLiveDisplay("tabs")}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🗂️ Tabbed View
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manual navigation with tabs. Click tabs to switch between
                    sections.
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisplayModeDialogOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Slide Time Selection Dialog */}
        <Dialog
          open={slideTimeDialogOpen}
          onClose={() => { setSlideTimeDialogOpen(false); setSlideConfig(defaultSlideConfig()); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>⏱️ Configure Slideshow</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which slides to show and for how long each is displayed.
            </Typography>

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", px: 1, pb: 0.5, borderBottom: 1, borderColor: "divider", mb: 1 }}>
              <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>Slide</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 60, textAlign: "center" }}>Show</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 140, textAlign: "center" }}>Duration</Typography>
            </Box>

            <Stack spacing={0.5}>
              {SLIDE_DEFS.map((slide) => {
                const cfg = slideConfig[slide.key] || { enabled: true, duration: 15 };
                return (
                  <Box key={slide.key} sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, borderRadius: 1, bgcolor: cfg.enabled ? "action.selected" : "transparent" }}>
                    <Typography variant="body2" sx={{ flex: 1, color: cfg.enabled ? "text.primary" : "text.disabled" }}>
                      {slide.label}
                    </Typography>
                    <Box sx={{ width: 60, display: "flex", justifyContent: "center" }}>
                      <Checkbox
                        checked={cfg.enabled}
                        onChange={(e) => setSlideConfig(prev => ({ ...prev, [slide.key]: { ...prev[slide.key], enabled: e.target.checked } }))}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
                      <Select
                        value={cfg.duration}
                        onChange={(e) => setSlideConfig(prev => ({ ...prev, [slide.key]: { ...prev[slide.key], duration: Number(e.target.value) } }))}
                        size="small"
                        disabled={!cfg.enabled}
                        sx={{ width: 130, fontSize: "0.8rem" }}
                      >
                        {[5, 10, 15, 20, 30, 45, 60].map(s => (
                          <MenuItem key={s} value={s}>{s}s</MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {SLIDE_DEFS.filter(s => slideConfig[s.key]?.enabled).length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>Select at least one slide to show.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSlideTimeDialogOpen(false); setSlideConfig(defaultSlideConfig()); }}>
              Cancel
            </Button>
            <Button
              onClick={handleOpenSlideshow}
              variant="contained"
              disabled={SLIDE_DEFS.filter(s => slideConfig[s.key]?.enabled).length === 0}
            >
              Start Slideshow
            </Button>
          </DialogActions>
        </Dialog>

        {/* Score Match Dialog */}
        <Dialog
          open={scoreDialogOpen}
          onClose={() => setScoreDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Score Match</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {selectedMatchForScoring && (
                <>
                  <Alert severity="info">
                    Enter the game scores for this match
                  </Alert>

                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <Typography variant="body1" fontWeight="bold">
                            {getTeamName(selectedMatchForScoring.team1Id)}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            vs
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography variant="body1" fontWeight="bold">
                            {getTeamName(selectedMatchForScoring.team2Id)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Game Scores
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <TextField
                        label={getTeamName(selectedMatchForScoring.team1Id)}
                        type="number"
                        value={team1Games}
                        onChange={(e) =>
                          setTeam1Games(
                            Math.max(0, parseInt(e.target.value) || 0),
                          )
                        }
                        inputProps={{ min: 0 }}
                        sx={{ width: 150 }}
                      />
                      <Typography variant="h6">-</Typography>
                      <TextField
                        label={getTeamName(selectedMatchForScoring.team2Id)}
                        type="number"
                        value={team2Games}
                        onChange={(e) =>
                          setTeam2Games(
                            Math.max(0, parseInt(e.target.value) || 0),
                          )
                        }
                        inputProps={{ min: 0 }}
                        sx={{ width: 150 }}
                      />
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleScoreKnockoutMatch}
              disabled={team1Games === 0 && team2Games === 0}
            >
              Submit Score
            </Button>
          </DialogActions>
        </Dialog>

        {/* Schedule Dialog */}
        <TournamentScheduleDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          tournament={selectedTournamentForSchedule}
          onUpdate={handleScheduleDialogUpdate}
          readOnly={false}
        />

        {/* Knockout Seeding Preview Dialog */}
        <KnockoutSeedingPreview
          open={seedingPreviewOpen}
          onClose={() => setSeedingPreviewOpen(false)}
          numberOfGroups={typeof numberOfTeams === 'number' && typeof teamsPerGroup === 'number' && teamsPerGroup > 0 ? Math.ceil(numberOfTeams / teamsPerGroup) : 0}
          teamsPerGroup={typeof teamsPerGroup === 'number' ? teamsPerGroup : 0}
          knockoutFormatType={knockoutFormatType}
          mastersCupAdvanceCount={typeof mastersCupAdvanceCount === 'number' ? mastersCupAdvanceCount : 2}
          tourCupAdvanceCount={typeof tourCupAdvanceCount === 'number' ? tourCupAdvanceCount : 2}
          knockoutAdvanceCount={typeof knockoutAdvanceCount === 'number' ? knockoutAdvanceCount : 2}
          mastersCupAdvancementRules={getMastersCupAdvancementRules()}
          tourCupAdvancementRules={getTourCupAdvancementRules()}
          onSeedingChange={setCustomSeeding}
          initialCustomSeeding={customSeeding}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />

        {/* Player Checklist Dialog */}
        {(() => {
          if (!checklistTournament) return null;
          // Build flat player list from registeredTeams, sorted alphabetically
          const allPlayers: { playerId: string; playerName: string; teamName: string }[] = [];
          (checklistTournament.registeredTeams || []).forEach((team) => {
            const teamLabel = team.teamName || `${team.player1Name || "?"} & ${team.player2Name || "?"}`;
            if (team.player1Id) allPlayers.push({ playerId: team.player1Id, playerName: team.player1Name || team.player1Id, teamName: teamLabel });
            if (team.player2Id) allPlayers.push({ playerId: team.player2Id, playerName: team.player2Name || team.player2Id, teamName: teamLabel });
          });
          allPlayers.sort((a, b) => a.playerName.localeCompare(b.playerName));

          const searchTerm = checklistSearch.toLowerCase();
          const players = searchTerm
            ? allPlayers.filter((p) => p.playerName.toLowerCase().includes(searchTerm) || p.teamName.toLowerCase().includes(searchTerm))
            : allPlayers;

          const notShowed = allPlayers.filter((p) => !checklist[p.playerId]?.showed).length;
          const notPaid = allPlayers.filter((p) => !checklist[p.playerId]?.paid).length;

          return (
            <Dialog open={checklistDialogOpen} onClose={() => setChecklistDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Player Checklist — {checklistTournament.name}</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1.5}>
                  {/* Summary */}
                  <Alert severity={notShowed === 0 && notPaid === 0 ? "success" : "info"} sx={{ mb: 1 }}>
                    {notShowed === 0 && notPaid === 0
                      ? "All players have shown up and paid ✓"
                      : [
                          notShowed > 0 && `${notShowed} player${notShowed !== 1 ? "s" : ""} haven't shown up`,
                          notPaid > 0 && `${notPaid} player${notPaid !== 1 ? "s" : ""} haven't paid`,
                        ].filter(Boolean).join(" · ")}
                  </Alert>

                  {/* Search */}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by player or team name…"
                    value={checklistSearch}
                    onChange={(e) => setChecklistSearch(e.target.value)}
                    InputProps={{ startAdornment: <span style={{ marginRight: 6, color: "#aaa" }}>🔍</span> }}
                  />

                  {/* Header row */}
                  <Box sx={{ display: "flex", alignItems: "center", px: 1, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>Player</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ width: 80, textAlign: "center" }}>Showed Up</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ width: 80, textAlign: "center" }}>Paid</Typography>
                  </Box>

                  {/* Player rows */}
                  {players.map((p) => {
                    const state = checklist[p.playerId] || { showed: false, paid: false };
                    return (
                      <Box
                        key={p.playerId}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: state.showed && state.paid ? "success.50" : "background.paper",
                          border: 1,
                          borderColor: state.showed && state.paid ? "success.light" : "divider",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{p.playerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.teamName}</Typography>
                        </Box>
                        <Box sx={{ width: 80, display: "flex", justifyContent: "center" }}>
                          <Checkbox
                            checked={state.showed}
                            onChange={(e) => handleChecklistToggle(p.playerId, "showed", e.target.checked, checklistTournament)}
                            disabled={checklistSaving}
                            size="small"
                            color="success"
                          />
                        </Box>
                        <Box sx={{ width: 80, display: "flex", justifyContent: "center" }}>
                          <Checkbox
                            checked={state.paid}
                            onChange={(e) => handleChecklistToggle(p.playerId, "paid", e.target.checked, checklistTournament)}
                            disabled={checklistSaving}
                            size="small"
                            color="primary"
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setChecklistDialogOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        {/* Registrations Overview Dialog */}
        <Dialog open={overviewDialogOpen} onClose={() => setOverviewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Registrations — {overviewTournament?.name}
          </DialogTitle>
          <DialogContent dividers>
            {overviewLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>}
            {!overviewLoading && overviewData && (
              <Stack spacing={3}>
                {/* Confirmed Teams */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    ✅ Confirmed Teams ({overviewData.confirmedTeams.length})
                  </Typography>
                  {overviewData.confirmedTeams.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No confirmed teams yet.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {overviewData.confirmedTeams.map((team: any, i: number) => (
                        <Paper key={team.id} variant="outlined" sx={{ p: 1.5, borderColor: team.adminAdded ? "warning.light" : undefined }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={700}>#{i + 1}</Typography>
                            <Typography variant="body2" fontWeight={600}>{team.teamName}</Typography>
                            {team.adminAdded && (
                              <Chip label="Added by Admin" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            )}
                            <Box sx={{ flex: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {[team.player1, team.player2].map((p: any, pi: number) => (
                                <Box key={pi} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography variant="body2">{p.name}</Typography>
                                  {p.isNew && <Chip label="New Player" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                                  {p.isTournamentAccount && <Chip label="Tournament Account" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Divider />

                {/* Pending Invitations */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    ⏳ Pending Invitations ({overviewData.pendingInvitations.length})
                  </Typography>
                  {overviewData.pendingInvitations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No pending invitations.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {overviewData.pendingInvitations.map((inv: any) => (
                        <Paper key={inv.id} variant="outlined" sx={{ p: 1.5, borderColor: inv.expired ? "error.light" : "warning.light" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
                                {/* Sender */}
                                <Typography variant="body2" fontWeight={600}>{inv.sender.name}</Typography>
                                {inv.sender.isTournamentAccount && <Chip label="Tournament Account" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />}
                                <Typography variant="body2" color="text.secondary">invited</Typography>
                                {/* Recipient */}
                                <Typography variant="body2" fontWeight={600}>{inv.recipient.name}</Typography>
                                {inv.recipient.isNewPlayer
                                  ? <Chip label="New Player" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                                  : inv.recipient.isTournamentAccount
                                    ? <Chip label="Tournament Account" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />
                                    : <Chip label="Existing Player" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                                }
                              </Box>
                              {inv.teamName && <Typography variant="caption" color="text.secondary">Team: {inv.teamName}</Typography>}
                              {inv.personalMessage && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: "italic" }}>"{inv.personalMessage}"</Typography>}
                            </Box>
                            <Chip
                              label={inv.expired ? "Expired" : `${inv.hoursLeft}h ${inv.minutesLeft}m left`}
                              size="small"
                              color={inv.expired ? "error" : inv.hoursLeft < 4 ? "warning" : "default"}
                            />
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOverviewDialogOpen(false); if (overviewTournament) handleOpenOverview(overviewTournament); }}>
              Refresh
            </Button>
            <Button onClick={() => setOverviewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TournamentPointsConfig />
      </TabPanel>
    </Box>
  );
}

export default Tournaments;
